import React, { useState, useMemo, useRef, useEffect, useLayoutEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { useAuth } from "./AuthContext";
import { usePortalCalculations, sampleAtMonth, PORTAL_GROWTH_SLIDER_MAX } from "./usePortalCalculations";
import PortalChart from "./PortalChart";
import BoxSlider, { BOX_SLIDER_GLOBAL_STYLE } from "./BoxSlider";
import { getSavedStrategies, savePortalStrategy, deletePortalStrategy } from "./dataStorage";
import { db } from "./firebase";
import landingConfig from "./legend.landing.json";
import MobilePortalTutorialOverlay from "./MobilePortalTutorialOverlay.js";

const MOBILE_PORTAL_TUTORIAL_KEY = "sparro_mobile_portal_tutorial_v2";
const MOBILE_PORTAL_TUTORIAL_LEGACY_KEY = "sparro_mobile_portal_tutorial_v1";
const MOBILE_PORTAL_DEMO_DEBT = 12345;
const DEBT_LOCK_STORAGE_PREFIX = "sparro_locked_total_debt_";

function getSafeRect(node) {
  if (!node || typeof node.getBoundingClientRect !== "function") return null;
  try {
    return node.getBoundingClientRect();
  } catch {
    return null;
  }
}

function toPositiveDebtNumber(raw) {
  const n = Number(raw);
  if (!Number.isFinite(n)) return null;
  if (n <= 0) return null;
  return Math.round(n);
}

function isMobilePortalTutorialDone() {
  if (typeof window === "undefined") return true;
  try {
    if (window.localStorage.getItem(MOBILE_PORTAL_TUTORIAL_KEY) === "1") return true;
    if (window.localStorage.getItem(MOBILE_PORTAL_TUTORIAL_LEGACY_KEY) === "1") return true;
  } catch {
    return true;
  }
  return false;
}

// Mobile Info: tap opent een gecentreerde modal (portal) zodat lange teksten altijd leesbaar zijn en wrappen.
function MobileInfo({ text }) {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  const overlay =
    open &&
    createPortal(
      <div
        role="presentation"
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 10050,
          background: "rgba(15, 23, 42, 0.55)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding:
            "max(16px, env(safe-area-inset-top, 0px)) max(16px, env(safe-area-inset-right, 0px)) max(24px, env(safe-area-inset-bottom, 0px)) max(16px, env(safe-area-inset-left, 0px))",
          boxSizing: "border-box",
        }}
        onClick={() => setOpen(false)}
      >
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Toelichting"
          onClick={(e) => e.stopPropagation()}
          style={{
            width: "100%",
            maxWidth: 400,
            maxHeight: "min(78dvh, 560px)",
            overflowY: "auto",
            WebkitOverflowScrolling: "touch",
            overscrollBehavior: "contain",
            background: "#111827",
            color: "#F9FAFB",
            border: "1px solid #374151",
            borderRadius: 14,
            padding: "18px 20px",
            fontSize: 15,
            lineHeight: 1.55,
            boxShadow: "0 24px 48px rgba(0,0,0,0.35)",
            wordBreak: "break-word",
          }}
        >
          {text}
        </div>
      </div>,
      document.body
    );

  return (
    <span
      style={{ position: "relative", display: "inline-flex", alignItems: "center", marginLeft: 6, flexShrink: 0, alignSelf: "center" }}
    >
      <span
        role="button"
        tabIndex={0}
        onClick={(e) => {
          e.stopPropagation();
          setOpen((o) => !o);
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setOpen((o) => !o);
          }
        }}
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          width: 16,
          height: 16,
          borderRadius: "50%",
          background: "#ffffff",
          color: "#374151",
          fontSize: 10,
          fontWeight: 600,
          border: "1px solid #e5e7eb",
          cursor: "pointer",
          lineHeight: 1,
        }}
      >
        i
      </span>
      {overlay}
    </span>
  );
}

const TILE_IDS = [
  {
    id: "debt",
    label: "Studieschuld",
    valueKey: "totalDebt",
    fmt: "eur",
    info:
      "Het totaal dat je nu in de portal als studieschuld hebt ingevoerd. Alle tegels en de grafiek berekenen door op basis van deze schuld, je aflosschema en je overige keuzes.",
  },
  {
    id: "monthly",
    label: "Maandelijkse aflossing",
    valueKey: "constrainedMonthlyPayment",
    fmt: "eur",
    info:
      "Wat je hier ziet, is wat de portal uit je invoer berekent: je maandelijkse aflossing op de studieschuld (minimaal het wettelijke minimum als je dat zo hebt ingesteld). " +
      "Staat ‘Investeren naast aflossen’ aan, dan staat achter de slash ook je maandelijkse inleg. " +
      "Het zijn startbedragen aan het begin van het plan; eventuele jaarlijkse stijging van aflossing of inleg zit in alle vervolgberekeningen.",
  },
  {
    id: "years",
    label: "Jaar tot aflossing",
    valueKey: "payoffYears",
    info:
      "Het aantal jaar tot je schuld volgens dit scenario is afgelost. De portal herberekent dit zodra je onderaan iets wijzigt. Bij beleggen naast aflossen kan de lening eerder dicht zijn als je beleggingen de restschuld overtreffen.",
  },
  {
    id: "totalCost",
    label: "Totale kosten",
    valueKey: "totalCostWithInvestments",
    fmt: "eur",
    info:
      "Alles wat je in deze looptijd uitbetaalt volgens het scenario: aflossing, rente én (als van toepassing) de ingelegde beleggingsbedragen — exclusief geschatte beleggingsgroei. Handig om scenario’s met elkaar te vergelijken.",
  },
  {
    id: "interest",
    label: "Rente betaald",
    valueKey: "interestAtEnd",
    fmt: "eur",
    info:
      "Totale rente die je over de looptijd betaalt; maandelijks berekend over de resterende schuld. Lagere aflossing of uitstel kan dit bedrag verhogen — dat zie je hier en in de grafiek terug.",
  },
  {
    id: "investment",
    label: "Waarde investeringen op einde looptijd",
    valueKey: "finalInvestmentValue",
    fmt: "eur",
    info:
      "Geschatte waarde van je beleggingen op het moment dat de lening volgens dit plan is afgelost (of aan het eind van de looptijd). Gebaseerd op je invoer inclusief de in deze tool gebruikte groeiaanname (bijv. 6% per jaar).",
  },
];

const MENU_OPTIONS = [
  { id: "basisinformatie", label: "Basisinformatie input" },
  { id: "aflossingen", label: "Maandbedrag aflossen" },
  { id: "uitstellen", label: "Aflossingen uitstellen" },
  { id: "investeren", label: "Investeren naast aflossen" },
  { id: "eenmalig", label: "Eenmalige aflossingen" },
];

/** Tutorial: uitleg per onderdelen-bucket (volgorde = MENU_OPTIONS). */
const CATEGORY_TUTORIAL_EXPLANATIONS = {
  basisinformatie:
    "Dit zijn de uitgangspunten van je lening: hoe hoog is je studieschuld, kies je het 15- of 35-jarig aflossingsschema, en ben je al begonnen met aflossen? Die gegevens bepalen samen met de rest wat het wettelijke minimum is en hoe snel je schuld daalt.",
  aflossingen:
    "Hier kies je hoeveel je vrijwillig per maand aflost boven het minimum, of je dat bedrag jaarlijks laat meestijgen, en eventueel een maximum zodat je maandlast binnen een grens blijft. Alles wat je hier wijzigt, zie je direct terug in de tegels en de grafiek.",
  uitstellen:
    "Hier plan je een periode waarin je (gedeeltelijk) geen aflossing betaalt. De portal rekent rente over die periode mee, zodat je ziet wat uitstel doet met je looptijd en totale kosten — handig bij tijdelijk minder inkomen.",
  investeren:
    "Als je wilt, kun je naast aflossen ook maandelijks beleggen met een door jou gekozen groeipercentage. De portal laat zien hoe dat samen met aflossen uitpakt voor je schuld en looptijd (binnen de gekozen grenzen).",
  eenmalig:
    "Hier voeg je eenmalige extra aflossingen toe en kies je in welk jaar van je plan ze vallen — bijvoorbeeld een bonus of een meevaller. Zo zet je incidentele bedragen in je scenario.",
};

const MENU_CATEGORY_ORDER_IDS = MENU_OPTIONS.map((o) => o.id);

const WEGWIJZER_COMPLETED_KEY = "sparro_wegwijzer_completed_v1";
const WEGWIJZER_MINIMIZED_KEY = "sparro_wegwijzer_minimized_v1";
const WEGWIJZER_SCREEN_MARGIN = 12;
const WEGWIJZER_CARD_MAX_BODY_PX = 280;

const WEGWIJZER_INTRO_TITLE = "Welkom bij de wegwijzer";
const WEGWIJZER_INTRO_BODY =
  "De wegwijzer helpt je op weg: we lopen samen de belangrijkste velden door en je kunt meteen waarden invullen of kiezen die in je strategie worden gezet. Tik op Verder om te beginnen.";

const WEGWIJZER_WRAPUP_TITLE = "Strategie en invoer";
const WEGWIJZER_WRAPUP_BODY =
  "Kijk hierboven wat deze strategie voor jouw aflostraject betekent, en pas vervolgens hieronder in de invoersectie alles aan naar jouw wensen.";

/**
 * Rondleiding na onboarding: één veld per stap, volgorde vaste categorieën.
 * refKey koppelt aan wegwijzerTargetRefs in MobilePortalPage.
 */
function getWegwijzerSteps(calc) {
  const steps = [
    {
      id: "stelsel",
      pane: "basisinformatie",
      refKey: "stelsel",
      title: "Stelsel (aflostermijn)",
      body:
        "Kies het aflossingsschema dat bij jouw lening hoort: 15 of 35 jaar. Dit bepaalt onder meer het standaardrentepercentage en hoe het wettelijke minimum wordt berekend.",
    },
    {
      id: "alBegonnen",
      pane: "basisinformatie",
      refKey: "alBegonnen",
      title: "Al begonnen met aflossen?",
      body:
        "Geef aan of je al eerder bent begonnen met aflossen. Zo ja, kun je invullen hoeveel jaar je al bezig bent — de portal rekent dan met de resterende looptijd.",
    },
    {
      id: "jaarAlBezig",
      pane: "basisinformatie",
      refKey: "jaarAlBezig",
      title: "Hoe lang ben je al bezig?",
      body:
        "Vul in hoeveel jaar je al aflost. Daarmee wordt je resterende termijn in dit scenario ingeschat.",
    },
    {
      id: "zelfRente",
      pane: "basisinformatie",
      refKey: "zelfRente",
      title: "Zelf rente instellen",
      body:
        "Standaard gebruikt de tool het DUO-rentepercentage bij jouw stelsel. Als je wilt, kun je hier afwijkende rentes of renteperiodes invullen.",
    },
    {
      id: "minDuo",
      pane: "aflossingen",
      refKey: "minDuo",
      title: "Minimale aflossing DUO",
      body:
        "Dit is het wettelijke minimum dat je volgens de DUO-regels minimaal per maand moet aflossen. Je kunt het aanpassen als het in jouw situatie anders is.",
    },
    {
      id: "maandelijkseAflossing",
      pane: "aflossingen",
      refKey: "maandelijkseAflossing",
      title: "Maandelijkse aflossing",
      body:
        "Kies hoeveel je vrijwillig per maand aflost boven het minimum. Sleep de schuif of typ een bedrag — de tegels en grafiek werken direct mee.",
    },
    {
      id: "jaarlijkseStijgingAflossing",
      pane: "aflossingen",
      refKey: "jaarlijkseStijgingAflossing",
      title: "Jaarlijkse stijging maandbedrag",
      body:
        "Optioneel: laat je maandbedrag jaarlijks meestijgen (bijvoorbeeld met inkomensgroei). Zet dit aan en kies een percentage.",
    },
    {
      id: "delayYears",
      pane: "uitstellen",
      refKey: "delayYears",
      title: "Jaren uitstel",
      body:
        "Kies hoeveel volledige jaren je de aflossing uitstelt (max. 5 jaar). Laat op 0 staan als je geen uitstel wilt. Tijdens uitstel loopt de rente wel door.",
    },
    {
      id: "maandelijkseInleg",
      pane: "investeren",
      refKey: "maandelijkseInleg",
      title: "Maandelijkse inleg",
      body:
        "Stel in hoeveel je per maand investeert (of laat op €0 als je niet belegt naast aflossen). De portal laat zien hoe dit samen met aflossen uitpakt.",
    },
    {
      id: "startkapitaal",
      pane: "investeren",
      refKey: "startkapitaal",
      title: "Startkapitaal (optioneel)",
      body:
        "Heb je al een belegd bedrag? Vul dat hier in zodat de groei vanaf nu realistischer wordt meegenomen.",
    },
    {
      id: "investGroei",
      pane: "investeren",
      refKey: "investGroei",
      title: "Jaarlijkse stijging investeringen",
      body:
        "Optioneel: laat je maandelijkse inleg jaarlijks met een vast percentage stijgen, los van je aflossing.",
    },
    {
      id: "eenmalig",
      pane: "eenmalig",
      refKey: "eenmalig",
      title: "Eenmalige aflossingen",
      body:
        "Voeg extra eenmalige aflossingen toe (bedrag + jaar in je plan). Gebruik + voor meerdere boekingen.",
    },
  ];

  return steps.filter((s) => {
    if (s.id === "jaarAlBezig") return calc.hasStartedRepaying;
    if (s.id === "investGroei") return calc.enableSeparateGrowth;
    return true;
  });
}

/** Menu-stap invoer-tutorial: vaste hoogte voor kaart boven het sheet. */
const INPUT_MENU_TUTORIAL_CARD_FIXED_HEIGHT_PX = 212;
/** Veld-stap: kaart boven invoerpaneel, vaste hoogte. */
const INPUT_FIELD_TUTORIAL_CARD_FIXED_HEIGHT_PX = 256;
/** Intro na chart: kaart bovenaan (Kies categorie pulse). */
const INPUT_INTRO_TUTORIAL_TOP_CARD_HEIGHT_PX = 236;
/** Kaart verticaal centreren in band (schermtop ↔ menutop / invoerpaneeltop). */
const TUTORIAL_FLOAT_BAND_TOP_PAD_PX = 12;
const TUTORIAL_FLOAT_BAND_GAP_PX = 12;
/** Strategie opslaan-tutorial: kaart onder titelbalk, vaste hoogte. */
const HEADER_SAVE_TUTORIAL_CARD_FIXED_HEIGHT_PX = 300;
const HEADER_SAVE_BLUR_HEADER_EXPAND_PX = 2;
/** Ruimte tussen titelbalk en infokaart. */
const HEADER_SAVE_TUTORIAL_TOP_MARGIN_PX = 10;

function expandRectUniform(r, pad) {
  return {
    left: r.left - pad,
    top: r.top - pad,
    width: r.width + 2 * pad,
    height: r.height + 2 * pad,
  };
}

function formatValue(val, fmt) {
  if (val == null || (typeof val === "number" && isNaN(val))) return "—";
  if (fmt === "eur") return `€ ${Math.round(val).toLocaleString("nl-NL")}`;
  return String(val);
}

function monthlyTileTitle(calc) {
  return calc.enableInvestment ? "Maandelijkse aflossing / investering" : "Maandelijkse aflossing";
}

function MonthlyTileValueRow({ calc }) {
  const repay = formatValue(calc.constrainedMonthlyPayment, "eur");
  if (!calc.enableInvestment) {
    return <>{repay}</>;
  }
  const inv = formatValue(calc.monthlyInvestment, "eur");
  return (
    <>
      {repay}
      {" / "}
      {inv}
    </>
  );
}

export default function MobilePortalPage({ demoMode = false }) {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const uid = user?.uid ?? null;
  const isDemoMode = demoMode === true;
  const [loadedStrategy, setLoadedStrategy] = useState(null);
  const calc = usePortalCalculations(loadedStrategy);
  const calcRef = useRef(calc);
  calcRef.current = calc;
  const [viewMode, setViewMode] = useState("summary"); // 'summary' | 'chart'
  const [mainView, setMainView] = useState("portal"); // 'portal' | 'saved' | 'strategySummary' | 'faq'
  const [faqOpenIndices, setFaqOpenIndices] = useState([]);
  const [menuOpen, setMenuOpen] = useState(false);
  const [headerMenuOpen, setHeaderMenuOpen] = useState(false);
  const [selectedBottomPane, setSelectedBottomPane] = useState("basisinformatie");
  const [savedStrategies, setSavedStrategies] = useState([]);
  const [saveMessage, setSaveMessage] = useState(null);
  const [monthlyPaymentInput, setMonthlyPaymentInput] = useState("");
  const [showSaveNameModal, setShowSaveNameModal] = useState(false);
  const [saveNameValue, setSaveNameValue] = useState("");
  const [isSavingStrategy, setIsSavingStrategy] = useState(false);
  const [logoNameSvg, setLogoNameSvg] = useState(null);
  /** Vaste topbalk (fixed): hoogte voor layout-spacer zodat inhoud niet onder de balk schuift. */
  const headerBarRef = useRef(null);
  const [headerBarHeight, setHeaderBarHeight] = useState(64);
  const bottomScrollRef = useRef(null);
  const categoryMenuSheetRef = useRef(null);
  const tutorialModusTilesRef = useRef(null);
  const tutorialChartBtnRef = useRef(null);
  const tutorialCategoryBtnRef = useRef(null);
  const tutorialSaveBtnRef = useRef(null);
  const tutorialSaveConfirmBtnRef = useRef(null);
  const saveNameModalPanelRef = useRef(null);
  const touchStartYRef = useRef(null);
  const chartAreaRef = useRef(null);
  const stableViewportRef = useRef(
    typeof window !== "undefined" ? window.innerHeight : 640
  );
  const [viewportHeight, setViewportHeight] = useState(() =>
    typeof window !== "undefined" ? window.innerHeight : 640
  );

  /** null = uit / geladen; 'welcome' | 'modus' | 'input' */
  const [portalTutorialPhase, setPortalTutorialPhase] = useState(undefined);
  const [tutorialHoleRect, setTutorialHoleRect] = useState(null);
  const [tutorialWinSize, setTutorialWinSize] = useState(() =>
    typeof window !== "undefined" ? { w: window.innerWidth, h: window.innerHeight } : { w: 390, h: 844 }
  );
  /** Categorieën al geopend tijdens tutorial (voor Doorgaan = eerste nog niet gezien). */
  const tutorialSeenCategoryIdsRef = useRef(new Set());
  /** Invoer-tutorial: eerst pulse op Kies categorie, dan menu, dan velden. */
  const [inputTutorialSubStep, setInputTutorialSubStep] = useState("menu");
  /** Modus summary: infokaart exact over invoerpaneel (meet met bottomScrollRef). */
  const [tutorialModusFillRect, setTutorialModusFillRect] = useState(null);
  /** Veld-tutorial: twee gaten voor complement-blur [kaart, invoerpaneel]. */
  const [tutorialFieldBlurExcludeRects, setTutorialFieldBlurExcludeRects] = useState(null);
  /** Intro invoer: alles wazig behalve invoerpaneel (één gat). */
  const [tutorialInputIntroBlurExcludeRects, setTutorialInputIntroBlurExcludeRects] = useState(null);
  /** Menu-stap: kaart verticaal gecentreerd tussen schermtop en sheet; complement-blur = [sheet, kaart]. */
  const [tutorialMenuCardTopPx, setTutorialMenuCardTopPx] = useState(null);
  const [tutorialMenuBlurExcludeRects, setTutorialMenuBlurExcludeRects] = useState(null);
  /** Afstand viewport-onderkant → tutorialkaart-onderkant (veld-stap). */
  const [tutorialInputCardBottomPx, setTutorialInputCardBottomPx] = useState(null);
  /** Intro invoer: zelfde bottom-anker als categorie-stappen. */
  const [tutorialIntroCardBottomPx, setTutorialIntroCardBottomPx] = useState(null);
  /** headerSave: complement-blur alleen titelbalk — kaart ligt op z-index boven blur, geen gat nodig. */
  const [tutorialHeaderSaveBlurExcludeRects, setTutorialHeaderSaveBlurExcludeRects] = useState(null);
  /** Naam-modal: alles wazig behalve het witte dialoogvenster. */
  const [tutorialSaveModalBlurExcludeRects, setTutorialSaveModalBlurExcludeRects] = useState(null);
  /** Strategie-samenvatting wrapup: uitlegtekst ingeklapt. */
  const [strategyWrapupCollapsed, setStrategyWrapupCollapsed] = useState(false);
  const [debtLockLoading, setDebtLockLoading] = useState(true);
  const [debtLockRequired, setDebtLockRequired] = useState(false);
  const [debtLockInput, setDebtLockInput] = useState("");
  const [debtLockError, setDebtLockError] = useState("");
  const [debtLockSaving, setDebtLockSaving] = useState(false);
  const [lockedDebtAmount, setLockedDebtAmount] = useState(null);

  const wegwijzerTargetRefs = useRef({});
  const wegwijzerCardRef = useRef(null);
  const [wegwijzerCardLayout, setWegwijzerCardLayout] = useState(null);
  const [wegwijzerMinimized, setWegwijzerMinimized] = useState(() => {
    if (typeof window === "undefined") return false;
    try {
      return window.sessionStorage.getItem(WEGWIJZER_MINIMIZED_KEY) === "1";
    } catch {
      return false;
    }
  });
  const [wegwijzerCloseConfirmOpen, setWegwijzerCloseConfirmOpen] = useState(false);
  const [wegwijzerCompleted, setWegwijzerCompleted] = useState(() => {
    if (typeof window === "undefined") return false;
    try {
      return window.localStorage.getItem(WEGWIJZER_COMPLETED_KEY) === "1";
    } catch {
      return false;
    }
  });
  const [wegwijzerStepId, setWegwijzerStepId] = useState(null);
  /** intro → stappen → wrapup (laatste infobox bij eerste veld). */
  const [wegwijzerPhase, setWegwijzerPhase] = useState("intro");

  /** Na login/signup blijft de scrollpositie van /login soms op het document staan; portal-start moet bovenaan (titelbalk zichtbaar). */
  useLayoutEffect(() => {
    const resetScroll = () => {
      const se = document.scrollingElement;
      if (se) se.scrollTop = 0;
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
      window.scrollTo(0, 0);
    };
    resetScroll();
    const id = requestAnimationFrame(resetScroll);
    return () => cancelAnimationFrame(id);
  }, []);

  /**
   * Layouthoogte stabiel houden: bij virtueel toetsenbord daalt innerHeight sterk.
   * Die daling negeren voorkomt dat tegels/modusknoppen/invulvlak omhoog springen en blijven staan.
   * Wel updaten bij terugkerende hoogte, rotatie en pageshow (bfcache).
   */
  useEffect(() => {
    const apply = (h) => {
      const v = Math.max(320, Math.round(h));
      stableViewportRef.current = v;
      setViewportHeight(v);
    };

    const onResize = () => {
      const inner = window.innerHeight;
      const prev = stableViewportRef.current;
      if (inner >= prev * 0.88) {
        apply(inner);
        return;
      }
      if (inner < prev * 0.78) {
        return;
      }
      apply(inner);
    };

    const onOrientation = () => {
      setTimeout(() => apply(window.innerHeight), 350);
    };

    const onPageShow = (e) => {
      if (e.persisted) apply(window.innerHeight);
    };

    window.addEventListener("resize", onResize);
    window.addEventListener("orientationchange", onOrientation);
    window.addEventListener("pageshow", onPageShow);

    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("orientationchange", onOrientation);
      window.removeEventListener("pageshow", onPageShow);
    };
  }, []);

  /** iOS/WebKit: na sluiten toetsenbord soms layoutviewport verschoven — scroll terug naar 0. */
  useEffect(() => {
    const vv = window.visualViewport;
    let t = null;
    const resetScroll = () => {
      window.scrollTo(0, 0);
      if (document.documentElement) document.documentElement.scrollTop = 0;
      if (document.body) document.body.scrollTop = 0;
    };
    const schedule = () => {
      if (t) clearTimeout(t);
      t = setTimeout(() => {
        resetScroll();
        t = null;
      }, 80);
    };

    vv?.addEventListener("resize", schedule);
    window.addEventListener("focusout", schedule, true);
    const onVis = () => {
      if (document.visibilityState === "visible") schedule();
    };
    document.addEventListener("visibilitychange", onVis);

    return () => {
      vv?.removeEventListener("resize", schedule);
      window.removeEventListener("focusout", schedule, true);
      document.removeEventListener("visibilitychange", onVis);
      if (t) clearTimeout(t);
    };
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (isDemoMode) {
      setPortalTutorialPhase("welcome");
      return;
    }
    setPortalTutorialPhase(isMobilePortalTutorialDone() ? null : "welcome");
  }, [isDemoMode]);

  useEffect(() => {
    if (!isDemoMode) return;
    calc.setTotalDebt(MOBILE_PORTAL_DEMO_DEBT);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- demo-prefill runs once for this mode
  }, [isDemoMode]);

  useEffect(() => {
    if (isDemoMode) {
      setDebtLockLoading(false);
      setDebtLockRequired(false);
      setLockedDebtAmount(null);
      return;
    }
    let cancelled = false;
    const readDebtLock = async () => {
      setDebtLockLoading(true);
      const storageKey = `${DEBT_LOCK_STORAGE_PREFIX}${uid || "anon"}`;
      const fromStorage = (() => {
        try {
          return window.localStorage.getItem(storageKey);
        } catch {
          return null;
        }
      })();
      const storageDebt = toPositiveDebtNumber(fromStorage);
      if (storageDebt) {
        if (cancelled) return;
        calc.setTotalDebt(storageDebt);
        setLockedDebtAmount(storageDebt);
        setDebtLockRequired(false);
        setDebtLockLoading(false);
        return;
      }
      if (uid && db && db.collection) {
        try {
          const snap = await db.collection("users").doc(uid).get();
          const raw = snap.exists ? snap.data()?.lockedTotalDebt : null;
          const firestoreDebt = toPositiveDebtNumber(raw);
          if (firestoreDebt) {
            if (cancelled) return;
            calc.setTotalDebt(firestoreDebt);
            setLockedDebtAmount(firestoreDebt);
            setDebtLockRequired(false);
            setDebtLockLoading(false);
            try {
              window.localStorage.setItem(storageKey, String(firestoreDebt));
            } catch {
              /* ignore */
            }
            return;
          }
        } catch {
          /* ignore and continue with prompt */
        }
      }
      if (cancelled) return;
      setDebtLockInput(calc.totalDebt > 0 ? String(Math.round(calc.totalDebt)) : "");
      setDebtLockRequired(true);
      setDebtLockLoading(false);
      setSelectedBottomPane("basisinformatie");
      setViewMode("summary");
    };
    readDebtLock();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run per account or mode switch
  }, [uid, isDemoMode]);

  const confirmAndLockDebt = useCallback(async () => {
    const debt = toPositiveDebtNumber(debtLockInput);
    if (!debt) {
      setDebtLockError("Vul een geldige studieschuld in (groter dan 0).");
      return;
    }
    const formatted = debt.toLocaleString("nl-NL");
    const ok = window.confirm(
      `Weet je zeker dat jouw ingegeven studieschuld van ${formatted} klopt? Je kunt deze later niet meer veranderen.`
    );
    if (!ok) return;
    setDebtLockSaving(true);
    setDebtLockError("");
    const storageKey = `${DEBT_LOCK_STORAGE_PREFIX}${uid || "anon"}`;
    try {
      if (uid && db && db.collection) {
        await db.collection("users").doc(uid).set(
          {
            lockedTotalDebt: debt,
            totalDebtLocked: true,
            totalDebtLockedAt: new Date().toISOString(),
          },
          { merge: true }
        );
      }
      try {
        window.localStorage.setItem(storageKey, String(debt));
      } catch {
        /* ignore */
      }
      calcRef.current.setTotalDebt(debt);
      setLockedDebtAmount(debt);
      setDebtLockRequired(false);
    } catch (e) {
      setDebtLockError("Opslaan lukte niet. Probeer het opnieuw.");
    } finally {
      setDebtLockSaving(false);
    }
  }, [debtLockInput, uid]);

  const setWegwijzerRef = useCallback((key) => (el) => {
    wegwijzerTargetRefs.current[key] = el;
  }, []);

  const handleWegwijzerIntroContinue = useCallback(() => {
    const steps = getWegwijzerSteps(calcRef.current);
    if (steps.length === 0) return;
    setWegwijzerPhase("steps");
    setWegwijzerStepId(steps[0].id);
    setSelectedBottomPane(steps[0].pane);
  }, []);

  const handleWegwijzerNext = useCallback(() => {
    if (wegwijzerPhase !== "steps") return;
    const steps = getWegwijzerSteps(calcRef.current);
    const idx = steps.findIndex((s) => s.id === wegwijzerStepId);
    if (idx < 0) return;
    if (idx === steps.length - 1) {
      setWegwijzerPhase("wrapup");
      setWegwijzerStepId(steps[0].id);
      setSelectedBottomPane(steps[0].pane);
      return;
    }
    setWegwijzerStepId(steps[idx + 1].id);
  }, [wegwijzerStepId, wegwijzerPhase]);

  const handleWegwijzerPrevious = useCallback(() => {
    const steps = getWegwijzerSteps(calcRef.current);
    if (steps.length === 0) return;
    if (wegwijzerPhase === "wrapup") {
      setWegwijzerPhase("steps");
      const last = steps[steps.length - 1];
      setWegwijzerStepId(last.id);
      setSelectedBottomPane(last.pane);
      return;
    }
    if (wegwijzerPhase !== "steps") return;
    const idx = steps.findIndex((s) => s.id === wegwijzerStepId);
    if (idx <= 0) {
      setWegwijzerPhase("intro");
      setWegwijzerStepId(null);
      return;
    }
    setWegwijzerStepId(steps[idx - 1].id);
  }, [wegwijzerStepId, wegwijzerPhase]);

  const startWegwijzerFromMenu = useCallback(() => {
    const steps = getWegwijzerSteps(calcRef.current);
    if (steps.length === 0) return;
    setHeaderMenuOpen(false);
    setMainView("portal");
    setViewMode("summary");
    try {
      window.localStorage.removeItem(WEGWIJZER_COMPLETED_KEY);
    } catch {
      /* ignore */
    }
    setWegwijzerCompleted(false);
    setWegwijzerPhase("intro");
    setWegwijzerStepId(null);
    setWegwijzerMinimized(false);
    try {
      window.sessionStorage.removeItem(WEGWIJZER_MINIMIZED_KEY);
    } catch {
      /* ignore */
    }
    setSelectedBottomPane("basisinformatie");
  }, []);

  const toggleWegwijzerMinimized = useCallback(() => {
    setWegwijzerMinimized((prev) => {
      const next = !prev;
      try {
        window.sessionStorage.setItem(WEGWIJZER_MINIMIZED_KEY, next ? "1" : "0");
      } catch {
        /* ignore */
      }
      return next;
    });
  }, []);

  const dismissWegwijzerConfirmed = useCallback(() => {
    try {
      window.localStorage.setItem(WEGWIJZER_COMPLETED_KEY, "1");
    } catch {
      /* ignore */
    }
    setWegwijzerCompleted(true);
    setWegwijzerPhase("intro");
    setWegwijzerStepId(null);
    setWegwijzerCloseConfirmOpen(false);
  }, []);

  const handleWegwijzerWrapupDone = useCallback(() => {
    dismissWegwijzerConfirmed();
  }, [dismissWegwijzerConfirmed]);

  useEffect(() => {
    if (wegwijzerPhase === "intro") return;
    if (!wegwijzerStepId || wegwijzerCompleted) return;
    const steps = getWegwijzerSteps(calcRef.current);
    if (steps.some((s) => s.id === wegwijzerStepId)) return;
    setWegwijzerStepId(steps[0]?.id ?? null);
  }, [
    wegwijzerStepId,
    wegwijzerCompleted,
    wegwijzerPhase,
    calc.hasStartedRepaying,
    calc.enableSeparateGrowth,
  ]);

  useEffect(() => {
    if ((wegwijzerStepId || wegwijzerPhase === "intro") && !wegwijzerCompleted) setViewMode("summary");
  }, [wegwijzerStepId, wegwijzerPhase, wegwijzerCompleted]);

  useLayoutEffect(() => {
    if (wegwijzerPhase === "intro" || !wegwijzerStepId || wegwijzerCompleted) return;
    const step = getWegwijzerSteps(calcRef.current).find((s) => s.id === wegwijzerStepId);
    if (!step) return;
    setSelectedBottomPane(step.pane);
    const id = requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const scrollEl = bottomScrollRef.current;
        const el = wegwijzerTargetRefs.current[step.refKey];
        if (!scrollEl || !el) return;
        const pad = 8;
        const p = scrollEl.getBoundingClientRect();
        const t = el.getBoundingClientRect();
        if (t.top < p.top + pad) {
          scrollEl.scrollTop += t.top - p.top - pad;
        } else if (t.bottom > p.bottom - pad) {
          scrollEl.scrollTop += t.bottom - p.bottom + pad;
        }
      });
    });
    return () => cancelAnimationFrame(id);
  }, [
    wegwijzerStepId,
    wegwijzerCompleted,
    wegwijzerPhase,
    calc.hasStartedRepaying,
    calc.enableSeparateGrowth,
  ]);

  useLayoutEffect(() => {
    const baseActive =
      !isDemoMode &&
      !debtLockLoading &&
      !debtLockRequired &&
      lockedDebtAmount != null &&
      portalTutorialPhase === null &&
      isMobilePortalTutorialDone() &&
      mainView === "portal" &&
      !wegwijzerCompleted;
    const steps = getWegwijzerSteps(calcRef.current);
    const stepOk =
      wegwijzerPhase === "intro" ||
      wegwijzerPhase === "wrapup" ||
      (wegwijzerStepId != null && steps.some((s) => s.id === wegwijzerStepId));
    const active = baseActive && stepOk;
    if (!active) {
      setWegwijzerCardLayout(null);
      return undefined;
    }
    const run = () => {
      const pane = bottomScrollRef.current;
      const card = wegwijzerCardRef.current;
      if (!pane || !card) return;
      const pr = pane.getBoundingClientRect();
      const vw = typeof window !== "undefined" ? window.innerWidth : 390;
      const horizontalInset = 16;
      const width = Math.max(220, Math.min(vw - horizontalInset * 2, vw - 24));
      const left = (vw - width) / 2;
      const h = card.offsetHeight;
      const gapAboveInput = 14;
      let top = pr.top - h - gapAboveInput;
      const minTop = WEGWIJZER_SCREEN_MARGIN;
      if (top < minTop) top = minTop;
      setWegwijzerCardLayout((prev) => {
        const next = { left, top, width };
        if (
          prev &&
          prev.left === next.left &&
          prev.width === next.width &&
          Math.abs(prev.top - next.top) < 1.5
        ) {
          return prev;
        }
        return next;
      });
    };
    run();
    const ro = new ResizeObserver(run);
    const paneEl = bottomScrollRef.current;
    const cardEl = wegwijzerCardRef.current;
    if (cardEl) ro.observe(cardEl);
    if (paneEl) ro.observe(paneEl);
    window.addEventListener("resize", run);
    window.addEventListener("scroll", run, true);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", run);
      window.removeEventListener("scroll", run, true);
    };
  }, [
    isDemoMode,
    debtLockLoading,
    debtLockRequired,
    lockedDebtAmount,
    portalTutorialPhase,
    mainView,
    wegwijzerCompleted,
    wegwijzerStepId,
    wegwijzerPhase,
    wegwijzerMinimized,
    viewportHeight,
    viewMode,
    calc.hasStartedRepaying,
    calc.enableSeparateGrowth,
  ]);

  useLayoutEffect(() => {
    const layoutPhases = new Set(["modus", "input", "headerSave"]);
    if (!layoutPhases.has(portalTutorialPhase)) {
      setTutorialHoleRect(null);
      setTutorialModusFillRect(null);
      return;
    }
    const update = () => {
      setTutorialWinSize({ w: window.innerWidth, h: window.innerHeight });
      if (portalTutorialPhase === "modus" && tutorialModusTilesRef.current) {
        setTutorialHoleRect(getSafeRect(tutorialModusTilesRef.current));
        if (bottomScrollRef.current && (viewMode === "summary" || viewMode === "chart")) {
          setTutorialModusFillRect(getSafeRect(bottomScrollRef.current));
        } else {
          setTutorialModusFillRect(null);
        }
      } else if (portalTutorialPhase === "input" && !menuOpen && bottomScrollRef.current) {
        if (inputTutorialSubStep === "introPulse" || inputTutorialSubStep === "field") {
          setTutorialHoleRect(null);
        } else {
          setTutorialHoleRect(getSafeRect(bottomScrollRef.current));
        }
        setTutorialModusFillRect(null);
      } else if (portalTutorialPhase === "input" && menuOpen) {
        setTutorialHoleRect(null);
        setTutorialModusFillRect(null);
      } else if (portalTutorialPhase === "headerSave" && headerBarRef.current) {
        setTutorialHoleRect(null);
        setTutorialModusFillRect(null);
      } else {
        setTutorialHoleRect(null);
        setTutorialModusFillRect(null);
      }
    };
    update();
    const t = window.setTimeout(update, 120);
    let ro;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(() => update());
      if (portalTutorialPhase === "modus" && tutorialModusTilesRef.current) {
        ro.observe(tutorialModusTilesRef.current);
      }
      if (portalTutorialPhase === "modus" && bottomScrollRef.current) {
        ro.observe(bottomScrollRef.current);
      }
      if (portalTutorialPhase === "input" && !menuOpen && bottomScrollRef.current) {
        ro.observe(bottomScrollRef.current);
      }
      if (portalTutorialPhase === "headerSave" && headerBarRef.current) {
        ro.observe(headerBarRef.current);
      }
    }
    window.addEventListener("resize", update);
    return () => {
      window.clearTimeout(t);
      window.removeEventListener("resize", update);
      ro?.disconnect();
    };
  }, [
    portalTutorialPhase,
    viewMode,
    menuOpen,
    inputTutorialSubStep,
    viewportHeight,
    headerBarHeight,
    showSaveNameModal,
  ]);

  useLayoutEffect(() => {
    if (typeof window === "undefined") return undefined;
    if (portalTutorialPhase !== "input" || menuOpen || inputTutorialSubStep !== "introPulse") {
      setTutorialInputIntroBlurExcludeRects(null);
      setTutorialIntroCardBottomPx(null);
      return undefined;
    }
    const measure = () => {
      const pane = bottomScrollRef.current;
      if (!pane) return;
      const inputR = getSafeRect(pane);
      if (!inputR) return;
      const h = window.innerHeight;
      const bottom = Math.round(Math.max(28, h - inputR.top + 10));
      setTutorialIntroCardBottomPx(bottom);
      setTutorialInputIntroBlurExcludeRects([inputR]);
    };
    measure();
    const t = window.setTimeout(measure, 120);
    window.addEventListener("resize", measure);
    let ro;
    if (typeof ResizeObserver !== "undefined" && bottomScrollRef.current) {
      ro = new ResizeObserver(() => measure());
      ro.observe(bottomScrollRef.current);
    }
    return () => {
      window.clearTimeout(t);
      window.removeEventListener("resize", measure);
      ro?.disconnect();
    };
  }, [portalTutorialPhase, menuOpen, inputTutorialSubStep, viewportHeight]);

  useLayoutEffect(() => {
    if (typeof window === "undefined") return undefined;
    if (portalTutorialPhase !== "input" || !menuOpen || inputTutorialSubStep !== "menu") {
      setTutorialMenuCardTopPx(null);
      setTutorialMenuBlurExcludeRects(null);
      return undefined;
    }
    const ch = INPUT_MENU_TUTORIAL_CARD_FIXED_HEIGHT_PX;
    const measure = () => {
      const sheet = categoryMenuSheetRef.current;
      if (!sheet) return;
      const r = getSafeRect(sheet);
      if (!r) return;
      const w = window.innerWidth;
      const bandMid = r.top / 2;
      let cardTop = Math.round(bandMid - ch / 2);
      const minTop = TUTORIAL_FLOAT_BAND_TOP_PAD_PX;
      const maxTop = r.top - TUTORIAL_FLOAT_BAND_GAP_PX - ch;
      cardTop = Math.max(minTop, Math.min(maxTop, cardTop));
      const cardR = { left: 16, top: cardTop, width: w - 32, height: ch };
      setTutorialMenuCardTopPx(cardTop);
      setTutorialMenuBlurExcludeRects([r, cardR]);
    };
    measure();
    const t = window.setTimeout(measure, 120);
    window.addEventListener("resize", measure);
    let ro;
    const sheetEl = categoryMenuSheetRef.current;
    if (typeof ResizeObserver !== "undefined" && sheetEl) {
      ro = new ResizeObserver(() => measure());
      ro.observe(sheetEl);
    }
    return () => {
      window.clearTimeout(t);
      window.removeEventListener("resize", measure);
      ro?.disconnect();
    };
  }, [portalTutorialPhase, menuOpen, inputTutorialSubStep, viewportHeight]);

  useLayoutEffect(() => {
    if (typeof window === "undefined") return undefined;
    if (portalTutorialPhase !== "input" || menuOpen || inputTutorialSubStep !== "field") {
      setTutorialInputCardBottomPx(null);
      setTutorialFieldBlurExcludeRects(null);
      return undefined;
    }
    const ch = INPUT_FIELD_TUTORIAL_CARD_FIXED_HEIGHT_PX;
    const run = () => {
      const pane = bottomScrollRef.current;
      if (!pane) return;
      const inputR = getSafeRect(pane);
      if (!inputR) return;
      const w = window.innerWidth;
      const h = window.innerHeight;
      const bottom = Math.round(Math.max(28, h - inputR.top + 10));
      setTutorialInputCardBottomPx(bottom);
      const top = h - bottom - ch;
      const cardR = { left: 16, top, width: w - 32, height: ch };
      setTutorialFieldBlurExcludeRects([cardR, inputR]);
    };
    run();
    const t = window.setTimeout(run, 80);
    window.addEventListener("resize", run);
    let ro;
    if (typeof ResizeObserver !== "undefined" && bottomScrollRef.current) {
      ro = new ResizeObserver(() => run());
      ro.observe(bottomScrollRef.current);
    }
    return () => {
      window.clearTimeout(t);
      window.removeEventListener("resize", run);
      ro?.disconnect();
    };
  }, [portalTutorialPhase, menuOpen, inputTutorialSubStep, viewportHeight]);

  useLayoutEffect(() => {
    if (typeof window === "undefined") return undefined;
    if (portalTutorialPhase !== "headerSave" || showSaveNameModal) {
      setTutorialHeaderSaveBlurExcludeRects(null);
      return undefined;
    }
    const run = () => {
      const headerEl = headerBarRef.current;
      if (!headerEl) return;
      const headerRect = getSafeRect(headerEl);
      if (!headerRect) return;
      const headerR = expandRectUniform(headerRect, HEADER_SAVE_BLUR_HEADER_EXPAND_PX);
      /* Alleen titelbalk uitsluiten; infokaart ligt op z-index boven de blur. */
      setTutorialHeaderSaveBlurExcludeRects([headerR]);
    };
    run();
    const t = window.setTimeout(run, 80);
    window.addEventListener("resize", run);
    let ro;
    if (typeof ResizeObserver !== "undefined" && headerBarRef.current) {
      ro = new ResizeObserver(() => run());
      ro.observe(headerBarRef.current);
    }
    return () => {
      window.clearTimeout(t);
      window.removeEventListener("resize", run);
      ro?.disconnect();
    };
  }, [portalTutorialPhase, showSaveNameModal, viewportHeight, headerBarHeight]);

  useLayoutEffect(() => {
    if (typeof window === "undefined") return undefined;
    if (!showSaveNameModal || portalTutorialPhase !== "headerSave") {
      setTutorialSaveModalBlurExcludeRects(null);
      return undefined;
    }
    const run = () => {
      const el = saveNameModalPanelRef.current;
      if (!el) return;
      const r = getSafeRect(el);
      if (!r) return;
      setTutorialSaveModalBlurExcludeRects([expandRectUniform(r, 4)]);
    };
    run();
    const t = window.setTimeout(run, 80);
    window.addEventListener("resize", run);
    let ro;
    if (typeof ResizeObserver !== "undefined" && saveNameModalPanelRef.current) {
      ro = new ResizeObserver(() => run());
      ro.observe(saveNameModalPanelRef.current);
    }
    return () => {
      window.clearTimeout(t);
      window.removeEventListener("resize", run);
      ro?.disconnect();
    };
  }, [showSaveNameModal, portalTutorialPhase, viewportHeight]);

  useEffect(() => {
    if (portalTutorialPhase !== "input" || inputTutorialSubStep !== "field") return;
    if (selectedBottomPane === "uitstellen" && calc.delayYears === 0) {
      calc.setDelayYears(1);
    } else if (
      selectedBottomPane === "investeren" &&
      (calc.monthlyInvestment || 0) === 0 &&
      (calc.startingInvestmentCapital || 0) === 0
    ) {
      calc.setMonthlyInvestment(10);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- tutorial: alleen specifieke calc-velden
  }, [
    portalTutorialPhase,
    inputTutorialSubStep,
    selectedBottomPane,
    calc.delayYears,
    calc.monthlyInvestment,
    calc.startingInvestmentCapital,
    calc.setDelayYears,
    calc.setMonthlyInvestment,
  ]);

  const finishMobilePortalTutorial = useCallback(() => {
    try {
      window.localStorage.setItem(MOBILE_PORTAL_TUTORIAL_KEY, "1");
    } catch {
      /* ignore */
    }
    setViewMode("summary");
    setSelectedBottomPane("basisinformatie");
    setMenuOpen(false);
    tutorialSeenCategoryIdsRef.current = new Set();
    setInputTutorialSubStep("menu");
    setPortalTutorialPhase(null);
    setStrategyWrapupCollapsed(false);
  }, []);

  const tutorialWrapupBackToPortal = useCallback(() => {
    finishMobilePortalTutorial();
    if (isDemoMode) {
      navigate("/signup");
      return;
    }
    setMainView("portal");
  }, [finishMobilePortalTutorial, isDemoMode, navigate]);

  const proceedToDemoWrapup = useCallback(() => {
    const c = calcRef.current;
    const strategyPayload = {
      id: `demo-${Date.now()}`,
      timestamp: new Date().toISOString(),
      name: "Demo strategie",
      totalDebt: c.totalDebt,
      repaymentPlan: c.repaymentPlan,
      hasStartedRepaying: c.hasStartedRepaying,
      yearsAlreadyRepaid: c.yearsAlreadyRepaid,
      oneTimeRepayments: (c.oneTimeRepayments || []).filter((r) => r && (r.amount || 0) > 0).length > 0
        ? c.oneTimeRepayments
        : [],
      minPayment: c.minPayment,
      monthlyPayment: c.monthlyPayment,
      enableGrowth: c.enableGrowth,
      growthRate: c.growthRate,
      enableSeparateGrowth: c.enableSeparateGrowth,
      investmentGrowthRate: c.investmentGrowthRate,
      enableInvestment: c.enableInvestment,
      monthlyInvestment: c.monthlyInvestment,
      enableDelay: c.enableDelay,
      delayYears: c.delayYears,
      delayMonths: c.delayMonths,
      startingInvestmentCapital: c.startingInvestmentCapital ?? 0,
      enableCustomRates: !!c.enableCustomRates,
      customRates: Array.isArray(c.customRates) && c.customRates.length > 0 ? c.customRates : [],
    };
    setLoadedStrategy(strategyPayload);
    setPortalTutorialPhase("strategyWrapup");
    setStrategyWrapupCollapsed(false);
    setMainView("strategySummary");
  }, []);

  const tutorialOnPrevious = useCallback(() => {
    if (portalTutorialPhase === "welcome") return;
    if (portalTutorialPhase === "modus") {
      setPortalTutorialPhase("welcome");
      return;
    }
    if (portalTutorialPhase === "input") {
      if (menuOpen) {
        setMenuOpen(false);
        setInputTutorialSubStep("introPulse");
        return;
      }
      if (inputTutorialSubStep === "introPulse") {
        setPortalTutorialPhase("modus");
        setViewMode("chart");
        tutorialSeenCategoryIdsRef.current = new Set();
        return;
      }
      const idx = MENU_CATEGORY_ORDER_IDS.indexOf(selectedBottomPane);
      if (idx <= 0) {
        setMenuOpen(true);
        setInputTutorialSubStep("menu");
        return;
      }
      const prevId = MENU_CATEGORY_ORDER_IDS[idx - 1];
      setSelectedBottomPane(prevId);
      return;
    }
    if (portalTutorialPhase === "headerSave") {
      if (showSaveNameModal) {
        setShowSaveNameModal(false);
        return;
      }
      setPortalTutorialPhase("input");
      setMenuOpen(true);
      setInputTutorialSubStep("menu");
    }
  }, [portalTutorialPhase, showSaveNameModal, menuOpen, selectedBottomPane, inputTutorialSubStep]);

  const skipCategoryMenuTutorial = useCallback(() => {
    const first = MENU_CATEGORY_ORDER_IDS[0];
    setSelectedBottomPane(first);
    tutorialSeenCategoryIdsRef.current = new Set(tutorialSeenCategoryIdsRef.current).add(first);
    setMenuOpen(false);
    setInputTutorialSubStep("field");
  }, []);

  const openBottomCategoryMenu = useCallback(() => {
    if (portalTutorialPhase === "input") {
      setInputTutorialSubStep("menu");
    }
    setMenuOpen(true);
  }, [portalTutorialPhase]);

  const restartMobilePortalTutorial = useCallback(() => {
    try {
      window.localStorage.removeItem(MOBILE_PORTAL_TUTORIAL_KEY);
      window.localStorage.removeItem(MOBILE_PORTAL_TUTORIAL_LEGACY_KEY);
    } catch {
      /* ignore */
    }
    setHeaderMenuOpen(false);
    setShowSaveNameModal(false);
    setMenuOpen(false);
    setMainView("portal");
    setViewMode("summary");
    tutorialSeenCategoryIdsRef.current = new Set();
    setInputTutorialSubStep("menu");
    setPortalTutorialPhase("welcome");
  }, []);

  const tutorialOnNext = useCallback(() => {
    if (portalTutorialPhase === "welcome") {
      setPortalTutorialPhase("modus");
      setViewMode("summary");
      return;
    }
    if (portalTutorialPhase === "modus") {
      if (viewMode === "summary") {
        setViewMode("chart");
        return;
      }
      setPortalTutorialPhase("input");
      setMenuOpen(false);
      setInputTutorialSubStep("introPulse");
      tutorialSeenCategoryIdsRef.current = new Set();
      return;
    }
    if (portalTutorialPhase === "input") {
      if (inputTutorialSubStep === "introPulse" && !menuOpen) {
        setMenuOpen(true);
        setInputTutorialSubStep("menu");
        return;
      }
      if (menuOpen) {
        const first = MENU_CATEGORY_ORDER_IDS[0];
        setSelectedBottomPane(first);
        tutorialSeenCategoryIdsRef.current = new Set(tutorialSeenCategoryIdsRef.current).add(first);
        setMenuOpen(false);
        setInputTutorialSubStep("field");
        return;
      }
      const idx = MENU_CATEGORY_ORDER_IDS.indexOf(selectedBottomPane);
      if (idx < 0) return;
      if (idx >= MENU_CATEGORY_ORDER_IDS.length - 1) {
        setPortalTutorialPhase("headerSave");
        return;
      }
      const nextId = MENU_CATEGORY_ORDER_IDS[idx + 1];
      setSelectedBottomPane(nextId);
      tutorialSeenCategoryIdsRef.current = new Set(tutorialSeenCategoryIdsRef.current).add(nextId);
      return;
    }
    if (portalTutorialPhase === "headerSave") {
      return;
    }
  }, [portalTutorialPhase, viewMode, menuOpen, selectedBottomPane, inputTutorialSubStep]);

  const openSaveStrategyModal = useCallback(() => {
    setSaveNameValue(
      "Strategie " +
        new Date().toLocaleDateString("nl-NL", { day: "numeric", month: "short", year: "numeric" })
    );
    setShowSaveNameModal(true);
  }, []);

  const mobilePortalTutorialOverlayProps = useMemo(() => {
    const phase = portalTutorialPhase;
    if (mainView === "strategySummary" && phase === "strategyWrapup") {
      return {
        step: "strategyWrapup",
        title: "Je strategie is opgeslagen",
        body:
          "De portal bewaart deze instellingen als een genoemd scenario. Hierboven zie je de belangrijkste cijfers en een korte toelichting. Onderaan deze pagina staat een echt aflosschema: per jaar zie je onder meer je betalingen, rente, investeringen en de resterende schuld. Zo kun je het verloop in één oogopslag volgen. Je vindt dit scenario later terug bij Opgeslagen strategieën, om te vergelijken of verder aan te passen.",
        cardPosition: "bottom",
        cardInsetPx: 8,
        cardMaxWidthPx: 600,
        scrollHintText:
          "Scroll op deze pagina naar beneden om het volledige aflosschema te bekijken.",
        welcomeFullBlur: false,
        blurRegionRect: null,
        nextDisabled: false,
        showNext: false,
        showPrevious: false,
        showContinue: false,
        hideCard: false,
        primaryAction: {
          label: isDemoMode ? "Account aanmaken" : "Aan de slag en terug naar de portal",
          onClick: tutorialWrapupBackToPortal,
          className: "tutorial-target-pulse",
        },
        wrapupCollapsible: true,
        wrapupCollapsed: strategyWrapupCollapsed,
        onWrapupCollapseToggle: () => setStrategyWrapupCollapsed((c) => !c),
        spotlightActive: false,
        holeRect: null,
        windowSize: tutorialWinSize,
        cardTopPx: undefined,
      };
    }

    if (mainView === "portal" && phase === "headerSave" && showSaveNameModal) {
      return {
        step: "headerSaveModal",
        hideCard: true,
        title: "",
        body: "",
        welcomeFullBlur: false,
        blurRegionRect: null,
        spotlightActive: true,
        blurExcludeRects: tutorialSaveModalBlurExcludeRects,
        holeRect: null,
        windowSize: tutorialWinSize,
        showNext: false,
        showPrevious: false,
        showContinue: false,
        primaryAction: null,
        cardPosition: "bottom",
        cardTopPx: undefined,
      };
    }

    if (mainView !== "portal") return null;
    if (phase !== "welcome" && phase !== "modus" && phase !== "input" && phase !== "headerSave") {
      return null;
    }

    let title = "";
    let body = "";
    let cardPosition = "bottom";
    let welcomeFullBlur = false;
    let nextDisabled = false;
    let showNext = true;
    let showContinue = false;
    let showPrevious = true;
    let hideCard = false;
    let spotlightActive = true;

    if (phase === "welcome") {
      title = "Welkom in de portal";
      body =
        "Deze portal is een reken- en vergelijk-tool voor je studieschuld: je vult je situatie in en ziet direct wat dat doet met looptijd, kosten en scenario’s. In een paar korte stappen laten we zien waar je resultaten ziet, waar je gegevens invult en hoe je een plan kunt bewaren.";
      cardPosition = "center";
      welcomeFullBlur = true;
      showPrevious = false;
    } else if (phase === "modus") {
      title = "Samenvatting en grafiek";
      if (viewMode === "summary") {
        body =
          "Bovenin zie je de belangrijkste uitkomsten van je huidige invoer — bijvoorbeeld maandlast, looptijd en kosten. Alles wat je straks in het onderste paneel aanpast, werkt hier direct door. Deze uitleg gebruikt dezelfde ruimte als dat invoerpaneel. Tik op Chart Modus of op Volgende om het verloop in een grafiek te bekijken.";
        cardPosition = "fill";
      } else {
        body =
          "De grafiek laat zien hoe restschuld en rente in de tijd verlopen, zodat je scenario’s beter kunt vergelijken. Tik op Volgende om naar invoer te gaan: daar stel je de aannames verder bij.";
        cardPosition = "fill";
      }
      nextDisabled = false;
    } else if (phase === "input") {
      if (inputTutorialSubStep === "introPulse" && !menuOpen) {
        title = "Invoer per onderwerp";
        body =
          "Onderaan stel je je lening en opties in. Tik op Kies categorie om een onderwerp te kiezen, of op Volgende om eerst het categorieënmenu te openen en daarna met het eerste onderwerp verder te gaan.";
        cardPosition = "bottom";
        spotlightActive = true;
        nextDisabled = false;
        showNext = true;
        showContinue = false;
        showPrevious = true;
      } else if (menuOpen) {
        title = "Invoer per onderwerp";
        body =
          "Kies een onderdeel om meteen naar dat invoerveld te gaan, of tik op Volgende om met het eerste onderwerp in de vaste volgorde te starten. Sluiten opent meteen basisinformatie.";
        cardPosition = "top";
        spotlightActive = true;
        nextDisabled = false;
        showNext = true;
        showContinue = false;
        showPrevious = true;
      } else {
        const lab = MENU_OPTIONS.find((o) => o.id === selectedBottomPane)?.label ?? "";
        title = lab || "Invoer per onderwerp";
        body = CATEGORY_TUTORIAL_EXPLANATIONS[selectedBottomPane] ?? "";
        cardPosition = "bottom";
        nextDisabled = false;
        showNext = true;
        showContinue = false;
        showPrevious = true;
        spotlightActive = true;
      }
    } else if (phase === "headerSave") {
      title = "Strategie opslaan";
      body =
        "Zo bewaar je dit scenario met een eigen naam. Je kunt het later terugvinden, naast andere plannen vergelijken of verder aanpassen. Tik op Doorgaan of op Strategie opslaan in de titelbalk om een naam te kiezen en te bevestigen.";
      cardPosition = "top";
      nextDisabled = true;
      showNext = false;
      showContinue = true;
      spotlightActive = true;
    }

    const stepKey =
      phase === "input"
        ? menuOpen
          ? "input-menu"
          : inputTutorialSubStep === "introPulse"
            ? "input-intro-pulse"
            : `input-field-${selectedBottomPane}`
        : phase;

    let bottomCardFixedHeightCalc;
    if (
      phase === "input" &&
      !menuOpen &&
      inputTutorialSubStep === "introPulse" &&
      typeof tutorialIntroCardBottomPx === "number"
    ) {
      bottomCardFixedHeightCalc = INPUT_INTRO_TUTORIAL_TOP_CARD_HEIGHT_PX;
    } else if (
      phase === "input" &&
      !menuOpen &&
      inputTutorialSubStep === "field" &&
      typeof tutorialInputCardBottomPx === "number"
    ) {
      bottomCardFixedHeightCalc = INPUT_FIELD_TUTORIAL_CARD_FIXED_HEIGHT_PX;
    } else {
      bottomCardFixedHeightCalc = undefined;
    }

    const cardBottomPxOut =
      phase === "input" &&
            !menuOpen &&
            inputTutorialSubStep === "introPulse" &&
            typeof tutorialIntroCardBottomPx === "number"
          ? tutorialIntroCardBottomPx
          : phase === "input" &&
              !menuOpen &&
              inputTutorialSubStep === "field" &&
              typeof tutorialInputCardBottomPx === "number"
            ? tutorialInputCardBottomPx
            : undefined;

    const cardFillRectOut = phase === "modus" && tutorialModusFillRect ? tutorialModusFillRect : null;

    const cardTopPxOut =
      phase === "headerSave" && !showSaveNameModal
        ? Math.round(headerBarHeight + HEADER_SAVE_TUTORIAL_TOP_MARGIN_PX)
        : phase === "input" && menuOpen && typeof tutorialMenuCardTopPx === "number"
          ? tutorialMenuCardTopPx
          : undefined;

    const topCardFixedHeightOut =
      phase === "headerSave" && !showSaveNameModal
        ? HEADER_SAVE_TUTORIAL_CARD_FIXED_HEIGHT_PX
        : phase === "input" && menuOpen && typeof tutorialMenuCardTopPx === "number"
          ? INPUT_MENU_TUTORIAL_CARD_FIXED_HEIGHT_PX
          : undefined;

    return {
      step: stepKey,
      title,
      body,
      cardPosition,
      cardFillRect: cardFillRectOut,
      cardTopPx: cardTopPxOut,
      topCardFixedHeight: topCardFixedHeightOut,
      topCardMaxHeight: undefined,
      bottomCardTranslateYPx: 0,
      bottomCardFixedHeight: bottomCardFixedHeightCalc,
      cardBottomPx: cardBottomPxOut,
      blurRegionRect: null,
      blurExcludeRects:
        phase === "headerSave" && !showSaveNameModal && tutorialHeaderSaveBlurExcludeRects
          ? tutorialHeaderSaveBlurExcludeRects
          : phase === "input" && menuOpen && inputTutorialSubStep === "menu" && tutorialMenuBlurExcludeRects
            ? tutorialMenuBlurExcludeRects
            : phase === "input" &&
                inputTutorialSubStep === "introPulse" &&
                !menuOpen &&
                tutorialInputIntroBlurExcludeRects
              ? tutorialInputIntroBlurExcludeRects
              : phase === "input" && !menuOpen && inputTutorialSubStep === "field" && tutorialFieldBlurExcludeRects
                ? tutorialFieldBlurExcludeRects
                : null,
      hideCard,
      welcomeFullBlur,
      nextDisabled,
      showNext,
      showContinue,
      showPrevious,
      primaryAction: null,
      onContinue:
        phase === "headerSave" && !showSaveNameModal
          ? (isDemoMode ? proceedToDemoWrapup : openSaveStrategyModal)
          : undefined,
      spotlightActive,
      holeRect: tutorialHoleRect,
      windowSize: tutorialWinSize,
      ...(phase === "headerSave" && !showSaveNameModal ? { cardInsetPx: 16, cardMaxWidthPx: 440 } : {}),
    };
  }, [
    mainView,
    portalTutorialPhase,
    viewMode,
    menuOpen,
    inputTutorialSubStep,
    selectedBottomPane,
    tutorialHoleRect,
    tutorialModusFillRect,
    tutorialFieldBlurExcludeRects,
    tutorialInputIntroBlurExcludeRects,
    tutorialMenuBlurExcludeRects,
    tutorialMenuCardTopPx,
    tutorialWinSize,
    strategyWrapupCollapsed,
    isDemoMode,
    tutorialInputCardBottomPx,
    tutorialIntroCardBottomPx,
    tutorialHeaderSaveBlurExcludeRects,
    tutorialSaveModalBlurExcludeRects,
    showSaveNameModal,
    tutorialWrapupBackToPortal,
    proceedToDemoWrapup,
    openSaveStrategyModal,
    headerBarHeight,
  ]);

  /** Alleen op portal-hoofdscherm: body vastzetten; op andere schermen (o.a. samenvatting) weer scrollbaar. */
  useEffect(() => {
    if (mainView === "portal") {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [mainView]);

  // Logo inline voor scherpe weergave op mobiel
  useEffect(() => {
    fetch("/logo-name.svg")
      .then((r) => r.text())
      .then((text) => {
        const scaled = text.replace(
          /<svg([^>]*)>/,
          (match) => {
            let attrs = match
              .replace(/\swidth="[^"]*"/, ' width="100%"')
              .replace(/\sheight="[^"]*"/, ' height="100%"');
            if (!/width=/.test(attrs)) attrs = attrs.replace(/<svg/, '<svg width="100%"');
            if (!/height=/.test(attrs)) attrs = attrs.replace(/<svg\s/, '<svg height="100%" ');
            return attrs.replace(/>$/, ' preserveAspectRatio="xMidYMid meet" style="display:block">');
          }
        );
        setLogoNameSvg(scaled);
      })
      .catch(() => {});
  }, []);

  const loadStrategies = React.useCallback(() => {
    getSavedStrategies(uid).then((list) => {
      setSavedStrategies((prev) => (list.length > 0 ? list : prev));
    });
  }, [uid]);

  useEffect(() => {
    if (uid) loadStrategies();
  }, [uid, loadStrategies]);

  useLayoutEffect(() => {
    const node = headerBarRef.current;
    if (!node || typeof ResizeObserver === "undefined") return undefined;
    const measure = () => {
      const h = node.getBoundingClientRect().height;
      if (h > 0) setHeaderBarHeight(Math.ceil(h));
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(node);
    return () => ro.disconnect();
  }, [saveMessage, mainView]);

  const selectMenuOption = useCallback(
    (id) => {
      setSelectedBottomPane(id);
      if (portalTutorialPhase === "input" && menuOpen) {
        tutorialSeenCategoryIdsRef.current = new Set(tutorialSeenCategoryIdsRef.current).add(id);
        setInputTutorialSubStep("field");
        setMenuOpen(false);
        return;
      }
      setMenuOpen(false);
    },
    [portalTutorialPhase, menuOpen]
  );

  const closeCategoryMenuOrSkipTutorial = useCallback(() => {
    if (portalTutorialPhase === "input") {
      skipCategoryMenuTutorial();
    } else {
      setMenuOpen(false);
    }
  }, [portalTutorialPhase, skipCategoryMenuTutorial]);

  // Zelfde tileVals-structuur als PC: totalCostWithInvestments, etc.
  const tileValues = useMemo(() => ({
    totalDebt: calc.totalDebt,
    constrainedMonthlyPayment: calc.constrainedMonthlyPayment,
    payoffYears: calc.payoffYears,
    totalCostWithInvestments: calc.totalCostWithInvestments,
    interestAtEnd: calc.interestAtEnd,
    finalInvestmentValue: calc.enableInvestment ? (calc.finalInvestmentValue ?? 0) : null,
  }), [calc.totalDebt, calc.constrainedMonthlyPayment, calc.payoffYears, calc.totalCostWithInvestments, calc.interestAtEnd, calc.enableInvestment, calc.finalInvestmentValue]);

  // Termijn = 15 of 35 jaar + jaren uitstel (zoals PC)
  const totalTermYears = calc.effectivePlanYears + (calc.enableDelay ? calc.delayYears : 0);
  const totalTermMonths = totalTermYears * 12;
  const finalBalance = calc.schedule?.length ? (calc.schedule[calc.schedule.length - 1]?.balance ?? 0) : 0;
  const isNotPaidOffWithinTerm =
    calc.payoffMonths >= totalTermMonths && finalBalance > 0 && !calc.payoffByInvestment;
  const showInvestmentPayoffBadge = (Number(calc.monthlyInvestment) || 0) > 0;

  const [sharedHoverYear, setSharedHoverYear] = useState(null);

  // Yearly table for strategy summary – exacte PC-methodologie (payoffYearCap, inv >= schuld => laatste jaar, balance 0)
  const yearlyTableData = useMemo(() => {
    const schedule = calc.schedule;
    if (!schedule || schedule.length === 0) return [];
    const gr = calc.growthRate / 1000;
    const igr = calc.enableSeparateGrowth ? calc.investmentGrowthRate / 1000 : gr;
    const delayMonthsTotal = calc.enableDelay ? calc.delayYears * 12 : 0;
    const maxYearsForData = calc.enableDelay ? totalTermYears : calc.effectivePlanYears;
    const oneTimeReps = calc.oneTimeRepayments || [];
    const payoffYearCap = Math.max(0, Math.floor((calc.payoffMonths - 1) / 12));
    const rows = [];
    for (let y = 0; y <= maxYearsForData; y++) {
      if (y > payoffYearCap) break;
      const yearEndMonth = Math.min((y + 1) * 12, calc.payoffMonths);
      const s = sampleAtMonth(schedule, yearEndMonth);
      let balanceAtEnd = Math.max(0, s.balance ?? 0);
      const invValue = s.investmentValue ?? 0;
      if (calc.enableInvestment && invValue > balanceAtEnd && balanceAtEnd > 0.01) balanceAtEnd = 0;
      if (y === payoffYearCap) balanceAtEnd = 0;
      const yearsElapsed = Math.max(0, y - delayMonthsTotal / 12);
      let monthlyPay = 0;
      let monthlyInv = 0;
      if ((y + 1) * 12 > delayMonthsTotal) {
        monthlyPay = calc.constrainedMonthlyPayment * (calc.enableGrowth ? Math.pow(1 + gr, Math.floor(yearsElapsed)) : 1);
        monthlyInv = calc.enableInvestment ? calc.monthlyInvestment * (calc.enableSeparateGrowth ? Math.pow(1 + igr, Math.floor(yearsElapsed)) : (calc.enableGrowth ? Math.pow(1 + gr, Math.floor(yearsElapsed)) : 1)) : 0;
      }
      const oneTimeThisYear = oneTimeReps
        .filter((r) => r && (r.amount || 0) > 0)
        .filter((r) => r.year === 0 || (r.year || 0) === y + 1)
        .reduce((sum, r) => sum + (Number(r.amount) || 0), 0);
      rows.push({
        year: y,
        monthlyPayment: Math.round(monthlyPay),
        monthlyInvestment: Math.round(monthlyInv),
        oneTimeThisYear: Math.round(oneTimeThisYear),
        investmentValue: Math.round(s.investmentValue ?? 0),
        interestPaid: Math.round(s.interestCumulative ?? 0),
        balance: Math.round(balanceAtEnd),
      });
      if (balanceAtEnd <= 0.01) break;
      if (calc.enableInvestment && invValue > (s.balance ?? 0) && (s.balance ?? 0) > 0.01) break;
      if (yearEndMonth >= calc.payoffMonths) break;
    }
    return rows;
  }, [calc.schedule, calc.payoffMonths, calc.enableDelay, calc.delayYears, calc.enableGrowth, calc.growthRate, calc.enableSeparateGrowth, calc.investmentGrowthRate, calc.constrainedMonthlyPayment, calc.enableInvestment, calc.monthlyInvestment, calc.oneTimeRepayments, totalTermYears, calc.effectivePlanYears]);

  // Richer strategy summary content (strategy + outcome, with bold) – used in strategy summary view
  const strategySummaryContent = useMemo(() => ({
    payment: Math.round(calc.constrainedMonthlyPayment),
    debt: Math.round(calc.totalDebt),
    growthPct: (calc.growthRate / 10).toFixed(1),
    investmentGrowthPct: (calc.investmentGrowthRate / 10).toFixed(1),
    delayYears: calc.delayYears,
    investment: Math.round(calc.monthlyInvestment),
    totalTermYears,
    payoffYears: calc.payoffYears,
    payoffRemainderMonths: calc.payoffRemainderMonths,
    interestTotal: Math.round(calc.interestAtEnd ?? 0),
    finalInv: Math.round(calc.finalInvestmentValue ?? 0),
    hasGrowth: calc.enableGrowth && calc.growthRate > 0,
    hasInvestmentGrowth: calc.enableSeparateGrowth && calc.investmentGrowthRate > 0,
    hasDelay: calc.enableDelay,
    hasInvestment: calc.enableInvestment,
    isNotPaidOff: isNotPaidOffWithinTerm,
  }), [calc.constrainedMonthlyPayment, calc.totalDebt, calc.enableGrowth, calc.growthRate, calc.enableSeparateGrowth, calc.investmentGrowthRate, calc.enableDelay, calc.delayYears, calc.enableInvestment, calc.monthlyInvestment, calc.payoffYears, calc.payoffRemainderMonths, calc.interestAtEnd, calc.finalInvestmentValue, totalTermYears, isNotPaidOffWithinTerm]);

  const container = {
    minHeight: "100vh",
    background: "#fff",
    paddingBottom: 24,
  };

  /** Lichte ruimte onder titelbalk, tussen knoppen en tegels, chart (niet de verticale padding van de modus-knoppen zelf). */
  const MOBILE_BLOCK_GAP = 6;

  const topToggle = {
    display: "flex",
    justifyContent: "center",
    padding: "5px 16px 6px",
    gap: 8,
    flexShrink: 0,
  };

  const toggleBtn = (active) => ({
    padding: "12px 24px",
    borderRadius: 9999,
    border: "none",
    background: active ? "#374151" : "#e5e7eb",
    color: active ? "#fff" : "#6b7280",
    fontSize: 15,
    fontWeight: 600,
    cursor: "pointer",
  });

  const summaryGrid = {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 12,
    padding: "0 20px 20px",
  };

  /** Vult de ruimte tussen modus-knoppen en invoerpaneel (2 kolommen × 3 rijen, gelijke hoogte). */
  const summaryGridPortal = {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gridTemplateRows: "repeat(3, minmax(0, 1fr))",
    flex: "1 1 0",
    minHeight: 0,
    gap: 10,
    padding: `${MOBILE_BLOCK_GAP}px 16px 10px`,
    alignContent: "stretch",
    boxSizing: "border-box",
  };

  const tile = {
    background: "linear-gradient(135deg, #3399FF 0%, #2288dd 100%)",
    borderRadius: 16,
    padding: "12px 12px",
    color: "#fff",
    boxShadow: "0 4px 12px rgba(83,113,255,0.25)",
    minHeight: 72,
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
  };

  const tilePortal = {
    background: "linear-gradient(135deg, #3399FF 0%, #2288dd 100%)",
    borderRadius: 12,
    padding: "12px 12px 14px",
    color: "#fff",
    boxShadow: "0 2px 8px rgba(83,113,255,0.22)",
    minHeight: 0,
    height: "100%",
    display: "flex",
    flexDirection: "column",
    justifyContent: "flex-start",
    alignItems: "stretch",
    boxSizing: "border-box",
    overflow: "hidden",
  };

  const menuButton = {
    display: "block",
    width: "calc(100% - 40px)",
    margin: "0 20px 20px",
    padding: "16px",
    borderRadius: 12,
    background: "linear-gradient(135deg, #3399FF 0%, #2288dd 100%)",
    color: "#fff",
    border: "none",
    fontSize: 16,
    fontWeight: 600,
    cursor: "pointer",
    boxShadow: "0 8px 24px rgba(83,113,255,0.35)",
  };

  const bottomPane = {
    marginLeft: 12,
    marginRight: 12,
    marginBottom: "max(10px, env(safe-area-inset-bottom, 0px))",
    marginTop: "auto",
    padding: "10px 14px max(12px, calc(12px + env(safe-area-inset-bottom, 0px)))",
    background: "#374151",
    borderRadius: 16,
    boxSizing: "border-box",
    /* Hoogte in px via viewportHeight (stabiel i.v.m. toetsenbord), zie style op ref-div. */
    flex: "0 0 auto",
    minHeight: 0,
    overflowY: "auto",
    overflowX: "hidden",
    WebkitOverflowScrolling: "touch",
    overscrollBehaviorY: "contain",
    touchAction: "pan-y", // Alleen verticaal scrollen; voorkomt dat horizontaal vegen naar vorige pagina gaat
  };

  const categoryPaneHeaderRow = {
    display: "flex",
    alignItems: "center",
    gap: 10,
    marginBottom: 10,
    width: "100%",
    minHeight: 40,
  };
  const categoryPaneTitle = {
    flex: 1,
    minWidth: 0,
    color: "#fff",
    fontSize: 15,
    fontWeight: 700,
    lineHeight: 1.25,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  };
  const categoryButtonNarrow = {
    flexShrink: 0,
    width: 132,
    boxSizing: "border-box",
    display: "block",
    padding: "9px 10px",
    borderRadius: 10,
    background: "linear-gradient(135deg, #3399FF 0%, #2288dd 100%)",
    color: "#fff",
    border: "none",
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
    boxShadow: "0 4px 12px rgba(83,113,255,0.3)",
    textAlign: "center",
  };

  /* Actief-knop: grijs = Inactief, blauw = Actief */
  const activeToggleBase = {
    padding: "9px 12px",
    borderRadius: 10,
    border: "none",
    fontSize: 14,
    fontWeight: 600,
    cursor: "pointer",
    marginBottom: 10,
    width: "auto",
    minWidth: 100,
  };
  const activeToggleInactive = {
    ...activeToggleBase,
    background: "#6b7280",
    color: "#fff",
  };
  const activeToggleActive = {
    ...activeToggleBase,
    background: "linear-gradient(135deg, #3399FF 0%, #2288dd 100%)",
    color: "#fff",
    boxShadow: "0 4px 12px rgba(83,113,255,0.3)",
  };

  const bottomRow = {
    marginBottom: 12,
  };

  const bottomLabel = {
    color: "rgba(255,255,255,0.9)",
    fontSize: 13,
    fontWeight: 600,
    marginBottom: 8,
  };
  // Geen margin op label in rijen met i-icoon, zodat icoon en titel exact op één hoogte gecentreerd zijn
  const bottomLabelInline = { ...bottomLabel, marginBottom: 0, lineHeight: 1.2 };

  const menuOverlay = {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.5)",
    zIndex: 10100,
    display: "flex",
    alignItems: "flex-end",
    justifyContent: "center",
  };

  const menuSheet = {
    background: "#fff",
    borderTopLeftRadius: 24,
    borderTopRightRadius: 24,
    width: "100%",
    maxHeight: "70vh",
    padding:
      "24px max(20px, env(safe-area-inset-right, 0px)) max(32px, calc(24px + env(safe-area-inset-bottom, 0px))) max(20px, env(safe-area-inset-left, 0px))",
    boxShadow: "0 -8px 32px rgba(0,0,0,0.15)",
  };

  const menuTitle = {
    fontSize: 18,
    fontWeight: 700,
    color: "#1f2937",
    marginBottom: 20,
  };

  const menuItem = (selected) => ({
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "14px 16px",
    borderRadius: 12,
    background: selected ? "rgba(83,113,255,0.1)" : "#f3f4f6",
    marginBottom: 8,
    cursor: "pointer",
    border: `2px solid ${selected ? "#3399FF" : "transparent"}`,
  });

  const monthlyMin = 0;
  const monthlyMax = 500;
  const invMax = 500;

  /** Fallback hoogte vóór meting van het flex-vlak (chart modus meet daarna met ResizeObserver). */
  const mobileChartHeight = useMemo(() => {
    const h = viewportHeight || 640;
    return Math.max(200, Math.min(320, Math.round(h * 0.36)));
  }, [viewportHeight]);

  const mobileChartFontSize = useMemo(() => ((viewportHeight || 640) < 720 ? 22 : 26), [viewportHeight]);

  const [chartFillHeight, setChartFillHeight] = useState(() => mobileChartHeight);

  useLayoutEffect(() => {
    if (mainView !== "portal" || viewMode !== "chart") return undefined;
    const el = chartAreaRef.current;
    if (!el) return undefined;
    const measure = () => {
      const { width: w, height: h } = el.getBoundingClientRect();
      if (w <= 0 || h <= 0) return;
      /* PortalChart viewBox-breedte is 900px; SVG schaalt op breedte, hoogte = w * (chartH/900). Vul het vlak: chartH = h * 900 / w. */
      const next = Math.max(140, Math.round((h * 900) / w));
      setChartFillHeight((prev) => (prev === next ? prev : next));
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [mainView, viewMode, viewportHeight, showInvestmentPayoffBadge, calc.enableInvestment]);

  const doSaveStrategy = async (name) => {
    const trimmed = String(name || "").trim();
    if (!trimmed) return;
    setIsSavingStrategy(true);
    setSaveMessage(null);
    const strategyPayload = {
      name: trimmed,
      totalDebt: calc.totalDebt,
      repaymentPlan: calc.repaymentPlan,
      hasStartedRepaying: calc.hasStartedRepaying,
      yearsAlreadyRepaid: calc.yearsAlreadyRepaid,
      oneTimeRepayments: (calc.oneTimeRepayments || []).filter((r) => r && (r.amount || 0) > 0).length > 0
        ? calc.oneTimeRepayments
        : [],
      minPayment: calc.minPayment,
      monthlyPayment: calc.monthlyPayment,
      enableGrowth: calc.enableGrowth,
      growthRate: calc.growthRate,
      enableSeparateGrowth: calc.enableSeparateGrowth,
      investmentGrowthRate: calc.investmentGrowthRate,
      enableInvestment: calc.enableInvestment,
      monthlyInvestment: calc.monthlyInvestment,
      enableDelay: calc.enableDelay,
      delayYears: calc.delayYears,
      delayMonths: calc.delayMonths,
      startingInvestmentCapital: calc.startingInvestmentCapital ?? 0,
      enableCustomRates: !!calc.enableCustomRates,
      customRates: Array.isArray(calc.customRates) && calc.customRates.length > 0 ? calc.customRates : [],
    };
    try {
      const ok = await savePortalStrategy(strategyPayload, uid);
      if (!ok.success) {
        setSaveMessage("Opslaan mislukt. " + (ok.error || "Probeer het opnieuw."));
        return;
      }
      const timestamp = new Date().toISOString();
      const saved = {
        id: ok.id,
        timestamp,
        name: strategyPayload.name,
        totalDebt: strategyPayload.totalDebt,
        repaymentPlan: strategyPayload.repaymentPlan,
        hasStartedRepaying: strategyPayload.hasStartedRepaying,
        yearsAlreadyRepaid: strategyPayload.yearsAlreadyRepaid,
        oneTimeRepayments: strategyPayload.oneTimeRepayments || [],
        minPayment: strategyPayload.minPayment,
        monthlyPayment: strategyPayload.monthlyPayment,
        enableGrowth: strategyPayload.enableGrowth,
        growthRate: strategyPayload.growthRate,
        enableSeparateGrowth: strategyPayload.enableSeparateGrowth,
        investmentGrowthRate: strategyPayload.investmentGrowthRate,
        enableInvestment: strategyPayload.enableInvestment,
        monthlyInvestment: strategyPayload.monthlyInvestment,
        enableDelay: strategyPayload.enableDelay,
        delayYears: strategyPayload.delayYears,
        delayMonths: strategyPayload.delayMonths,
        startingInvestmentCapital: strategyPayload.startingInvestmentCapital ?? 0,
        enableCustomRates: strategyPayload.enableCustomRates,
        customRates: strategyPayload.customRates || [],
      };
      setSavedStrategies((prev) => [saved, ...prev]);
      setLoadedStrategy(saved);
      setShowSaveNameModal(false);
      // Ga naar de in-app samenvatting (dezelfde weergave als bij "Samenvatting & aflosschema" op een opgeslagen strategie)
      setPortalTutorialPhase((ph) => (ph === "headerSave" ? "strategyWrapup" : ph));
      setStrategyWrapupCollapsed(false);
      setMainView("strategySummary");
    } catch (err) {
      console.error("Save strategy error:", err);
      setSaveMessage("Opslaan mislukt. Probeer het opnieuw.");
    } finally {
      setIsSavingStrategy(false);
    }
  };

  const HEADER_MENU_ITEMS = [
    { id: "portal", label: "Aflosstrategie samenstellen" },
    { id: "saved", label: "Opgeslagen strategieën" },
    { id: "faq", label: "FAQ" },
  ];
  const isPortalMainView = mainView === "portal";
  const routeScrollToBottomPane = useCallback((deltaY) => {
    const pane = bottomScrollRef.current;
    if (!pane || !Number.isFinite(deltaY) || deltaY === 0) return;
    pane.scrollTop += deltaY;
  }, []);
  const isInteractiveElement = (target) =>
    !!target?.closest?.("input, textarea, select, button, [role='button'], [data-no-scroll-proxy='true']");
  const handlePortalWheel = useCallback((e) => {
    const pane = bottomScrollRef.current;
    if (!pane || isInteractiveElement(e.target)) return;
    e.preventDefault();
    routeScrollToBottomPane(e.deltaY);
  }, [routeScrollToBottomPane]);
  const handlePortalTouchStart = useCallback((e) => {
    if (isInteractiveElement(e.target)) {
      touchStartYRef.current = null;
      return;
    }
    touchStartYRef.current = e.touches?.[0]?.clientY ?? null;
  }, []);
  const handlePortalTouchMove = useCallback((e) => {
    if (isInteractiveElement(e.target)) {
      touchStartYRef.current = null;
      return;
    }
    const prevY = touchStartYRef.current;
    if (prevY == null) return;
    const nextY = e.touches?.[0]?.clientY ?? prevY;
    const delta = prevY - nextY;
    routeScrollToBottomPane(delta);
    touchStartYRef.current = nextY;
  }, [routeScrollToBottomPane]);
  const handlePortalTouchEnd = useCallback(() => {
    touchStartYRef.current = null;
  }, []);

  const tutorialPulseChartBtn =
    !isDemoMode && mainView === "portal" && portalTutorialPhase === "modus" && viewMode === "summary";
  const tutorialPulseCategoryBtn =
    !isDemoMode &&
    mainView === "portal" &&
    portalTutorialPhase === "input" &&
    inputTutorialSubStep === "introPulse" &&
    !menuOpen;
  const tutorialPulseSaveStrategyBtn =
    !isDemoMode && mainView === "portal" && portalTutorialPhase === "headerSave" && !showSaveNameModal;
  const tutorialPulseSaveConfirmBtn =
    !isDemoMode && portalTutorialPhase === "headerSave" && showSaveNameModal && Boolean(saveNameValue.trim());
  const isDebtLocked = !isDemoMode && lockedDebtAmount != null;

  const wegwijzerStepsList = useMemo(
    () => getWegwijzerSteps(calcRef.current),
    [calc.hasStartedRepaying, calc.enableSeparateGrowth]
  );
  const currentWegwijzerStep =
    wegwijzerStepId != null ? wegwijzerStepsList.find((s) => s.id === wegwijzerStepId) ?? null : null;
  const wegwijzerOverlayActive =
    !isDemoMode &&
    !debtLockLoading &&
    !debtLockRequired &&
    lockedDebtAmount != null &&
    portalTutorialPhase === null &&
    isMobilePortalTutorialDone() &&
    mainView === "portal" &&
    !wegwijzerCompleted &&
    (wegwijzerPhase === "intro" ||
      wegwijzerPhase === "wrapup" ||
      (wegwijzerPhase === "steps" && wegwijzerStepId != null && currentWegwijzerStep != null));

  const renderWegwijzerStepInput = () => {
    const id = wegwijzerStepId;
    if (!id) return null;
    const wjRow = { display: "flex", gap: 8, flexWrap: "wrap", marginTop: 8 };
    const wjNum = {
      width: "100%",
      padding: "10px 12px",
      borderRadius: 8,
      border: "1px solid #bfdbfe",
      fontSize: 14,
      boxSizing: "border-box",
      background: "#fff",
      color: "#111827",
    };
    const selBtn = (label, selected, onClick) => (
      <button
        key={label}
        type="button"
        onClick={onClick}
        style={{
          ...activeToggleBase,
          flex: 1,
          minWidth: 100,
          marginBottom: 0,
          ...(selected ? activeToggleActive : activeToggleInactive),
        }}
      >
        {label}
      </button>
    );

    switch (id) {
      case "stelsel":
        return (
          <div style={wjRow}>
            {selBtn("15 jaar", calc.repaymentPlan === "15", () => calc.setRepaymentPlan("15"))}
            {selBtn("35 jaar", calc.repaymentPlan === "35", () => calc.setRepaymentPlan("35"))}
          </div>
        );
      case "alBegonnen":
        return (
          <div style={wjRow}>
            {selBtn("Nee", !calc.hasStartedRepaying, () => calc.setHasStartedRepaying(false))}
            {selBtn("Ja", calc.hasStartedRepaying, () => calc.setHasStartedRepaying(true))}
          </div>
        );
      case "jaarAlBezig":
        return (
          <input
            type="number"
            min={0}
            max={Math.max(0, calc.planYears - 1)}
            step={1}
            placeholder={`0 t/m ${calc.planYears - 1}`}
            value={calc.yearsAlreadyRepaid === 0 ? "" : calc.yearsAlreadyRepaid}
            onChange={(e) => {
              const v = e.target.value;
              const n = v === "" ? 0 : Math.min(Math.max(0, Number(v)), calc.planYears - 1);
              calc.setYearsAlreadyRepaid(n);
            }}
            style={wjNum}
          />
        );
      case "zelfRente": {
        const wjSelect = {
          flex: "1 1 140px",
          minWidth: 120,
          padding: "8px 10px",
          borderRadius: 8,
          border: "1px solid #bfdbfe",
          background: "#fff",
          color: "#111827",
          fontSize: 14,
          boxSizing: "border-box",
        };
        return (
          <>
            <div style={wjRow}>
              {selBtn("Standaard DUO-rente", !calc.enableCustomRates, () => calc.setEnableCustomRates(false))}
              {selBtn(
                "Zelf invullen",
                calc.enableCustomRates,
                () => {
                  calc.setEnableCustomRates(true);
                  if (!calc.customRates?.length) calc.setCustomRates([{ year: 0, ratePct: 0 }]);
                }
              )}
            </div>
            {!calc.enableCustomRates ? (
              <p style={{ margin: "8px 0 0", fontSize: 12, color: "#6b7280", lineHeight: 1.45 }}>
                Er wordt gebruikgemaakt van het rentepercentage van je aflosstelsel: {calc.planYears === 15 ? "2,21%" : "2,57%"}{" "}
                ({calc.planYears} jaar).
              </p>
            ) : null}
            {calc.enableCustomRates &&
              (() => {
                const segments = [...(calc.customRates || [])].filter((r) => r != null && typeof r.year === "number").sort((a, b) => a.year - b.year);
                const usedYears = new Set(segments.map((r) => r.year));
                const totalTermYears = calc.effectivePlanYears + (calc.enableDelay ? calc.delayYears : 0);
                const maxYear = Math.max(0, totalTermYears - 1);
                const list = segments.length === 0 ? [{ year: 0, ratePct: 0 }] : segments;
                return (
                  <div
                    style={{
                      marginTop: 10,
                      padding: 12,
                      borderRadius: 10,
                      border: "1px solid #bfdbfe",
                      background: "#f0f9ff",
                    }}
                  >
                    {list.map((seg, idx) => (
                      <div key={idx} style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 8, flexWrap: "wrap" }}>
                        {seg.year === 0 ? (
                          <span style={{ fontSize: 13, color: "#374151", flex: "1 1 140px" }}>Vanaf jaar 0</span>
                        ) : (
                          <select
                            value={seg.year}
                            onChange={(e) => {
                              const newY = Math.max(1, Math.min(maxYear, Number(e.target.value)));
                              const otherYears = new Set(segments.filter((s, i) => i !== idx).map((s) => s.year));
                              if (otherYears.has(newY)) return;
                              calc.setCustomRates((prev) => prev.map((s, i) => (i === idx ? { ...s, year: newY } : s)).sort((a, b) => a.year - b.year));
                            }}
                            style={wjSelect}
                          >
                            {Array.from({ length: maxYear + 1 }, (_, i) => i)
                              .filter((y) => y >= 1 && (y === seg.year || !segments.some((s, i) => i !== idx && s.year === y)))
                              .map((y) => (
                                <option key={y} value={y}>
                                  Vanaf jaar {y}
                                </option>
                              ))}
                          </select>
                        )}
                        <span style={{ display: "flex", alignItems: "center", gap: 4, marginLeft: "auto" }}>
                          <input
                            type="number"
                            min={0}
                            max={20}
                            step={0.01}
                            value={seg.ratePct === 0 ? "" : seg.ratePct}
                            onChange={(e) => {
                              const v = e.target.value;
                              const pct = v === "" ? 0 : Math.max(0, Math.min(20, Number(v)));
                              calc.setCustomRates((prev) => {
                                const next = prev.map((s, i) => (i === idx ? { ...s, ratePct: pct } : s));
                                return next.length ? next : [{ year: 0, ratePct: pct }];
                              });
                            }}
                            style={{ ...wjNum, width: 70, flexShrink: 0, padding: "8px 10px" }}
                          />
                          <span style={{ color: "#374151", fontSize: 14 }}>%</span>
                        </span>
                      </div>
                    ))}
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 4 }}>
                      <button
                        type="button"
                        onClick={() => {
                          const nextYear = Array.from({ length: maxYear + 1 }, (_, i) => i).find((y) => !usedYears.has(y));
                          if (nextYear !== undefined) calc.setCustomRates((prev) => [...prev, { year: nextYear, ratePct: 0 }].sort((a, b) => a.year - b.year));
                        }}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          width: 40,
                          height: 40,
                          borderRadius: 10,
                          border: "1px solid #93c5fd",
                          background: "#fff",
                          color: "#1d4ed8",
                          fontSize: 20,
                          cursor: "pointer",
                        }}
                      >
                        +
                      </button>
                      <span style={{ fontSize: 12, color: "#64748b" }}>Renteperiode toevoegen</span>
                    </div>
                  </div>
                );
              })()}
          </>
        );
      }
      case "minDuo":
        return (
          <input
            type="number"
            min={0}
            step={1}
            placeholder="Bijv. 120"
            value={calc.minPayment === 0 ? "" : calc.minPayment}
            onChange={(e) => {
              const v = e.target.value;
              calc.setMinPayment(v === "" ? 0 : Number(v));
            }}
            style={wjNum}
          />
        );
      case "maandelijkseAflossing":
        return (
          <>
            <BoxSlider
              key="wegwijzer-monthly-payment"
              min={Math.max(monthlyMin, calc.effectiveMinPayment)}
              max={monthlyMax}
              step={5}
              value={Math.min(monthlyMax, Math.max(calc.effectiveMinPayment, calc.monthlyPayment))}
              onChange={(v) => {
                setMonthlyPaymentInput("");
                calc.setMonthlyPayment(v);
              }}
            />
            <div style={{ fontSize: 12, color: "#6b7280", marginTop: 4 }}>
              € {calc.constrainedMonthlyPayment} / maand (min. €{calc.effectiveMinPayment})
            </div>
          </>
        );
      case "jaarlijkseStijgingAflossing":
        return (
          <>
            <div style={wjRow}>
              {selBtn("Uit", !calc.enableGrowth, () => calc.setEnableGrowth(false))}
              {selBtn("Aan", calc.enableGrowth, () => calc.setEnableGrowth(true))}
            </div>
            {calc.enableGrowth ? (
              <div style={{ marginTop: 8 }}>
                <BoxSlider
                  key="wegwijzer-growth-rate"
                  min={0}
                  max={PORTAL_GROWTH_SLIDER_MAX}
                  step={1}
                  value={Math.min(PORTAL_GROWTH_SLIDER_MAX, Math.max(0, calc.growthRate))}
                  onChange={(v) => calc.setGrowthRate(v)}
                />
                <div style={{ fontSize: 12, color: "#6b7280", marginTop: 4 }}>
                  {(calc.growthRate / 10).toFixed(1)}% per jaar (0% – 5%)
                </div>
              </div>
            ) : null}
          </>
        );
      case "delayYears":
        return (
          <>
            <BoxSlider
              key="wegwijzer-delay-years"
              min={0}
              max={5}
              step={1}
              value={Math.min(5, Math.max(0, calc.delayYears))}
              onChange={(v) => calc.setDelayYears(v)}
            />
            <div style={{ fontSize: 12, color: "#6b7280", marginTop: 4 }}>{calc.delayYears} jaar</div>
          </>
        );
      case "maandelijkseInleg":
        return (
          <>
            <BoxSlider
              key="wegwijzer-monthly-investment"
              min={0}
              max={invMax}
              step={10}
              value={Math.min(invMax, Math.max(0, calc.monthlyInvestment || 0))}
              onChange={(v) => calc.setMonthlyInvestment(v)}
            />
            <div style={{ fontSize: 12, color: "#6b7280", marginTop: 4 }}>€ {calc.monthlyInvestment || 0} / maand</div>
          </>
        );
      case "startkapitaal":
        return (
          <input
            type="number"
            min={0}
            step={100}
            placeholder="Bijv. 5000"
            value={calc.startingInvestmentCapital === 0 ? "" : calc.startingInvestmentCapital}
            onChange={(e) => {
              const v = e.target.value;
              calc.setStartingInvestmentCapital(v === "" ? 0 : Number(v));
            }}
            style={wjNum}
          />
        );
      case "investGroei":
        return (
          <>
            <div style={wjRow}>
              {selBtn("Uit", !calc.enableSeparateGrowth, () => calc.setEnableSeparateGrowth(false))}
              {selBtn("Aan", calc.enableSeparateGrowth, () => calc.setEnableSeparateGrowth(true))}
            </div>
            {calc.enableSeparateGrowth ? (
              <div style={{ marginTop: 8 }}>
                <BoxSlider
                  key="wegwijzer-investment-growth-rate"
                  min={0}
                  max={PORTAL_GROWTH_SLIDER_MAX}
                  step={1}
                  value={Math.min(PORTAL_GROWTH_SLIDER_MAX, Math.max(0, calc.investmentGrowthRate))}
                  onChange={(v) => calc.setInvestmentGrowthRate(v)}
                />
                <div style={{ fontSize: 12, color: "#6b7280", marginTop: 4 }}>
                  {(calc.investmentGrowthRate / 10).toFixed(1)}% per jaar (0% – 5%)
                </div>
              </div>
            ) : null}
          </>
        );
      case "eenmalig": {
        const r0 = calc.oneTimeRepayments?.[0] ?? { amount: 0, year: 1 };
        return (
          <input
            type="number"
            min={0}
            step={100}
            placeholder="Eerste eenmalige aflossing (€)"
            value={r0.amount === 0 ? "" : r0.amount}
            onChange={(e) => {
              const v = e.target.value;
              const amt = v === "" ? 0 : Math.max(0, Number(v));
              calc.setOneTimeRepayments((prev) => {
                const next = [...(prev?.length ? prev : [{ amount: 0, year: 1 }])];
                next[0] = { ...next[0], amount: amt };
                return next;
              });
            }}
            style={wjNum}
          />
        );
      }
      default:
        return null;
    }
  };

  const renderPortalSummaryTile = (t) => {
    const val = tileValues[t.valueKey];
    const isMonthlyTile = t.valueKey === "constrainedMonthlyPayment";
    const isYearsTile = t.valueKey === "payoffYears";
    const isInvestmentTile = t.valueKey === "finalInvestmentValue";
    const yearsDisplay =
      isYearsTile && isNotPaidOffWithinTerm
        ? "Niet binnen termijn"
        : isYearsTile
          ? calc.payoffRemainderMonths > 0
            ? `${calc.payoffYears} jaar ${calc.payoffRemainderMonths} mnd`
            : `${calc.payoffYears} jaar`
          : null;
    const valueDisplay = isMonthlyTile
      ? null
      : isYearsTile
        ? yearsDisplay
        : isInvestmentTile && (val == null || !calc.enableInvestment)
          ? "—"
          : formatValue(val, t.fmt);
    const titleDisplay = isMonthlyTile ? monthlyTileTitle(calc) : t.label;
    const valueFontSize = isYearsTile && isNotPaidOffWithinTerm ? 16 : 22;
    return (
      <div key={t.id} style={tilePortal}>
        <div
          style={{
            fontSize: 13,
            fontWeight: 600,
            opacity: 0.95,
            marginBottom: 8,
            display: "flex",
            alignItems: "flex-start",
            gap: 6,
            flexShrink: 0,
            minHeight: 46,
            lineHeight: 1.25,
          }}
        >
          <span style={{ lineHeight: 1.25, flex: 1, minWidth: 0 }}>{titleDisplay}</span>
          {t.info && (
            <span style={{ flexShrink: 0, lineHeight: 1, paddingTop: 1 }}>
              <MobileInfo text={t.info} />
            </span>
          )}
        </div>
        <div
          style={{
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "stretch",
          }}
        >
          <div
            style={{
              fontSize: valueFontSize,
              fontWeight: 700,
              lineHeight: 1.2,
              letterSpacing: "-0.02em",
              color: isYearsTile && isNotPaidOffWithinTerm ? "#FEE2E2" : undefined,
              wordBreak: "break-word",
            }}
          >
            {isMonthlyTile ? <MonthlyTileValueRow calc={calc} /> : valueDisplay}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div
      style={{
        ...container,
        ...(isPortalMainView
          ? {
              width: "100%",
              maxWidth: "100%",
              boxSizing: "border-box",
              height: viewportHeight,
              minHeight: viewportHeight,
              maxHeight: viewportHeight,
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
              paddingBottom: 0,
              overscrollBehavior: "none",
            }
          : {}),
        ...(mainView === "strategySummary"
          ? {
              height: "100vh",
              maxHeight: "100vh",
              overflowY: "auto",
              overflowX: "hidden",
              WebkitOverflowScrolling: "touch",
              overscrollBehaviorY: "contain",
            }
          : {}),
      }}
    >
      <div
        ref={headerBarRef}
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          zIndex: 10000,
          background: "#fff",
          borderBottom: "1px solid #e5e7eb",
          boxSizing: "border-box",
        }}
      >
        <header
          style={{
            paddingLeft: "max(20px, env(safe-area-inset-left, 0px))",
            paddingRight: "max(20px, env(safe-area-inset-right, 0px))",
            paddingTop: "max(16px, env(safe-area-inset-top, 0px))",
            paddingBottom: 16,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            minHeight: 52,
            boxSizing: "border-box",
          }}
        >
          <a href="/" target="_blank" rel="noopener noreferrer" style={{ display: "flex", alignItems: "center", textDecoration: "none" }} aria-label="Slimmer af">
            {logoNameSvg ? (
              <div style={{ height: 36, width: 135, flexShrink: 0 }} dangerouslySetInnerHTML={{ __html: logoNameSvg }} />
            ) : (
              <img src="/logo-name.svg" alt="Slimmer af" style={{ height: 36, width: "auto", display: "block" }} />
            )}
          </a>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            {mainView === "portal" && (
              <button
                ref={tutorialSaveBtnRef}
                type="button"
                className={tutorialPulseSaveStrategyBtn ? "tutorial-target-pulse" : undefined}
                onClick={openSaveStrategyModal}
                disabled={isSavingStrategy}
                style={{ background: "#3399FF", border: "none", borderRadius: 9999, fontSize: 13, color: "#fff", cursor: isSavingStrategy ? "wait" : "pointer", padding: "8px 16px", fontWeight: 500, opacity: isSavingStrategy ? 0.7 : 1 }}
              >
                {isSavingStrategy ? "Opslaan…" : "Strategie opslaan"}
              </button>
            )}
            <button
              type="button"
              onClick={() => setHeaderMenuOpen(true)}
              style={{ background: "none", border: "1px solid #d1d5db", borderRadius: 9999, fontSize: 14, color: "#374151", cursor: "pointer", padding: "8px 16px" }}
              aria-label="Menu"
            >
              Menu
            </button>
          </div>
        </header>
        {saveMessage && (
          <div
            style={{
              padding: "8px max(20px, env(safe-area-inset-right, 0px)) 8px max(20px, env(safe-area-inset-left, 0px))",
              background: saveMessage.includes("mislukt") ? "#FEE2E2" : "#D1FAE5",
              color: saveMessage.includes("mislukt") ? "#991B1B" : "#065F46",
              fontSize: 14,
              textAlign: "center",
            }}
          >
            {saveMessage}
          </div>
        )}
      </div>
      <div
        aria-hidden
        style={{
          height: headerBarHeight,
          flexShrink: 0,
          width: "100%",
          pointerEvents: "none",
        }}
      />

      {showSaveNameModal && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 10350,
            background: "rgba(0,0,0,0.38)",
            backdropFilter: "blur(14px)",
            WebkitBackdropFilter: "blur(14px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 20,
          }}
          onClick={() => !isSavingStrategy && setShowSaveNameModal(false)}
        >
          <div
            ref={saveNameModalPanelRef}
            style={{
              background: "#fff",
              borderRadius: 16,
              padding: 24,
              width: "100%",
              maxWidth: 360,
              boxShadow: "0 20px 40px rgba(0,0,0,0.2)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ fontSize: 18, fontWeight: 700, color: "#1f2937", marginBottom: 16 }}>Strategie opslaan</div>
            <p style={{ fontSize: 14, color: "#6b7280", marginBottom: 12 }}>Geef deze strategie een naam:</p>
            <input
              type="text"
              value={saveNameValue}
              onChange={(e) => setSaveNameValue(e.target.value)}
              placeholder="Bijv. Mijn aflosplan"
              autoFocus
              style={{
                width: "100%",
                boxSizing: "border-box",
                padding: "12px 14px",
                fontSize: 16,
                border: "2px solid #e5e7eb",
                borderRadius: 10,
                marginBottom: 20,
              }}
            />
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button
                type="button"
                onClick={() => !isSavingStrategy && setShowSaveNameModal(false)}
                style={{ padding: "10px 18px", fontSize: 14, color: "#374151", background: "#f3f4f6", border: "none", borderRadius: 8, cursor: "pointer", fontWeight: 500 }}
              >
                Annuleren
              </button>
              <button
                ref={tutorialSaveConfirmBtnRef}
                type="button"
                className={tutorialPulseSaveConfirmBtn ? "tutorial-target-pulse" : undefined}
                disabled={isSavingStrategy || !saveNameValue.trim()}
                onClick={() => doSaveStrategy(saveNameValue)}
                style={{
                  padding: "10px 18px",
                  fontSize: 14,
                  color: "#fff",
                  background: saveNameValue.trim() ? "#3399FF" : "#9ca3af",
                  border: "none",
                  borderRadius: 8,
                  cursor: saveNameValue.trim() && !isSavingStrategy ? "pointer" : "default",
                  fontWeight: 500,
                }}
              >
                {isSavingStrategy ? "Opslaan…" : "Opslaan"}
              </button>
            </div>
          </div>
        </div>
      )}

      {mainView === "saved" && (
        <div style={{ padding: "20px" }}>
          <div style={{ fontSize: 18, fontWeight: 700, color: "#1f2937", marginBottom: 16 }}>Opgeslagen strategieën</div>
          {savedStrategies.length === 0 ? (
            <p style={{ color: "#6b7280", fontSize: 14 }}>Nog geen strategieën opgeslagen. Sla je huidige instellingen op via &quot;Strategie opslaan&quot;.</p>
          ) : (
            <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
              {savedStrategies.map((s) => (
                <li
                  key={s.id}
                  style={{
                    padding: "14px 16px",
                    background: "#f9fafb",
                    borderRadius: 12,
                    marginBottom: 10,
                    display: "flex",
                    flexDirection: "column",
                    gap: 12,
                  }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
                    <div>
                      <div style={{ fontWeight: 600, color: "#1f2937", fontSize: 15 }}>{s.name}</div>
                      <div style={{ fontSize: 12, color: "#6b7280", marginTop: 2 }}>
                        {s.timestamp ? new Date(s.timestamp).toLocaleDateString("nl-NL", { day: "numeric", month: "short", year: "numeric" }) : ""}
                      </div>
                    </div>
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                      <button
                        type="button"
                        onClick={() => {
                          setLoadedStrategy(s);
                          setMainView("strategySummary");
                        }}
                        style={{
                          background: "#374151",
                          border: "none",
                          borderRadius: 8,
                          color: "#fff",
                          fontSize: 13,
                          fontWeight: 500,
                          padding: "8px 12px",
                          cursor: "pointer",
                        }}
                      >
                        Samenvatting & aflosschema
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setLoadedStrategy(s);
                          setMainView("portal");
                        }}
                        style={{
                          background: "#3399FF",
                          border: "none",
                          borderRadius: 8,
                          color: "#fff",
                          fontSize: 13,
                          fontWeight: 500,
                          padding: "8px 12px",
                          cursor: "pointer",
                        }}
                      >
                        Laad in portal
                      </button>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={async () => {
                      if (window.confirm(`Weet je zeker dat je "${s.name}" wilt verwijderen?`)) {
                        await deletePortalStrategy(s.id, uid);
                        const list = await getSavedStrategies(uid);
                        setSavedStrategies(list);
                      }
                    }}
                    style={{
                      background: "none",
                      border: "none",
                      padding: 0,
                      fontSize: 13,
                      color: "#6b7280",
                      cursor: "pointer",
                      alignSelf: "flex-start",
                      textDecoration: "underline",
                    }}
                  >
                    Verwijderen
                  </button>
                </li>
              ))}
            </ul>
          )}
          <button
            type="button"
            onClick={() => setMainView("portal")}
            style={{
              marginTop: 16,
              background: "none",
              border: "1px solid #d1d5db",
              borderRadius: 8,
              fontSize: 14,
              color: "#374151",
              padding: "10px 16px",
              cursor: "pointer",
            }}
          >
            Terug naar aflosstrategie
          </button>
        </div>
      )}

      {mainView === "faq" && (() => {
        const faqItems = (landingConfig.sections || []).find((s) => s.type === "faq" && s.id === "faq")?.items || [];
        const toggleFaq = (idx) => {
          setFaqOpenIndices((prev) =>
            prev.includes(idx) ? prev.filter((i) => i !== idx) : [...prev, idx]
          );
        };
        const renderFaqAnswer = (text) => {
          const hasDisclaimer = text.includes("**BELANGRIJK:");
          const parts = hasDisclaimer ? text.split("**BELANGRIJK:") : [text];
          return (
            <>
              {parts[0].split(/(\*[^*\n]+\*)/g).map((part, i) => {
                if (part.startsWith("*") && part.endsWith("*") && part.length > 2) {
                  return <em key={i} style={{ fontStyle: "italic" }}>{part.slice(1, -1)}</em>;
                }
                return <span key={i}>{part}</span>;
              })}
              {hasDisclaimer && (
                <div style={{
                  marginTop: 12,
                  padding: 12,
                  background: "#DBEAFE",
                  border: "2px solid #93C5FD",
                  borderRadius: 8,
                  color: "#1E40AF",
                  fontWeight: 600,
                  fontSize: 14
                }}>
                  BELANGRIJK: {parts[1].replace(/\*\*/g, "")}
                </div>
              )}
            </>
          );
        };
        return (
          <div style={{ padding: "20px", paddingBottom: 40 }}>
            <button
              type="button"
              onClick={() => setMainView("portal")}
              style={{
                marginBottom: 20,
                background: "none",
                border: "1px solid #d1d5db",
                borderRadius: 8,
                fontSize: 14,
                color: "#374151",
                padding: "8px 14px",
                cursor: "pointer",
              }}
            >
              ← Terug
            </button>
            <h1 style={{ margin: 0, marginBottom: 24, fontSize: 22, fontWeight: 700, color: "#111827" }}>FAQ</h1>
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {faqItems.map((qa, idx) => {
                const isOpen = faqOpenIndices.includes(idx);
                return (
                  <div
                    key={idx}
                    style={{
                      background: "#FFFFFF",
                      border: "1px solid #E5E7EB",
                      borderRadius: 16,
                      overflow: "hidden",
                      boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <button
                      type="button"
                      onClick={() => toggleFaq(idx)}
                      style={{
                        width: "100%",
                        padding: "16px 18px",
                        cursor: "pointer",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        fontWeight: 600,
                        fontSize: 15,
                        color: "#1F2937",
                        background: "#fff",
                        border: "none",
                        textAlign: "left",
                        fontFamily: "inherit",
                        minHeight: 52,
                        boxSizing: "border-box",
                      }}
                    >
                      <span style={{ lineHeight: 1.4, flex: 1, paddingRight: 12 }}>{qa.q}</span>
                      <span style={{
                        fontSize: 18,
                        color: "#3399FF",
                        transition: "transform 0.3s ease",
                        transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
                        flexShrink: 0,
                      }}>
                        ▼
                      </span>
                    </button>
                    {isOpen && (
                      <div
                        onClick={(e) => e.stopPropagation()}
                        style={{
                          padding: "0 18px 18px 18px",
                          color: "#4B5563",
                          lineHeight: 1.6,
                          whiteSpace: "pre-line",
                          fontSize: 14,
                          background: "#fff",
                        }}
                      >
                        {renderFaqAnswer(qa.a)}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}

      {mainView === "strategySummary" && loadedStrategy && (
        <div style={{ padding: "20px", paddingBottom: 40 }}>
          <button
            type="button"
            onClick={() => setMainView("saved")}
            style={{
              marginBottom: 16,
              background: "none",
              border: "1px solid #d1d5db",
              borderRadius: 8,
              fontSize: 14,
              color: "#374151",
              padding: "8px 14px",
              cursor: "pointer",
            }}
          >
            ← Terug naar opgeslagen strategieën
          </button>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: "#1f2937", marginBottom: 4 }}>Samenvatting Strategie</h1>
          <p style={{ fontSize: 15, color: "#6b7280", marginBottom: 20 }}>{loadedStrategy.name}</p>

          <div style={summaryGrid}>
            {TILE_IDS.map((t) => {
              const val = tileValues[t.valueKey];
              const isMonthlyTile = t.valueKey === "constrainedMonthlyPayment";
              const isYearsTile = t.valueKey === "payoffYears";
              const isInvestmentTile = t.valueKey === "finalInvestmentValue";
              const yearsDisplay = isYearsTile && isNotPaidOffWithinTerm
                ? "Niet binnen termijn"
                : isYearsTile
                  ? (calc.payoffRemainderMonths > 0
                    ? `${calc.payoffYears} jaar ${calc.payoffRemainderMonths} mnd`
                    : `${calc.payoffYears} jaar`)
                  : null;
              const valueDisplay = isMonthlyTile
                ? null
                : isYearsTile
                  ? yearsDisplay
                  : isInvestmentTile && (val == null || !calc.enableInvestment)
                    ? "—"
                    : formatValue(val, t.fmt);
              const titleDisplay = isMonthlyTile ? monthlyTileTitle(calc) : t.label;
              return (
                <div key={t.id} style={tile}>
                  <div style={{ fontSize: 11, opacity: 0.9, marginBottom: 4 }}>{titleDisplay}</div>
                  <div
                    style={{
                      fontSize: 22,
                      fontWeight: 700,
                      color: isYearsTile && isNotPaidOffWithinTerm ? "#FEE2E2" : undefined,
                      lineHeight: 1.25,
                      wordBreak: "break-word",
                    }}
                  >
                    {isMonthlyTile ? <MonthlyTileValueRow calc={calc} /> : valueDisplay}
                  </div>
                </div>
              );
            })}
          </div>

          <div style={{ marginTop: 20, padding: "16px", background: "#f9fafb", borderRadius: 12, fontSize: 14, lineHeight: 1.7, color: "#374151" }}>
            <p style={{ marginTop: 0, marginBottom: 12 }}>
              <strong>Wat is de strategie?</strong> Je lost <strong>€{strategySummaryContent.payment.toLocaleString("nl-NL")} per maand</strong> af op je studieschuld van <strong>€{strategySummaryContent.debt.toLocaleString("nl-NL")}</strong>.
              {strategySummaryContent.hasGrowth && (
                <> Je verhoogt je aflossing <strong>jaarlijks met {strategySummaryContent.growthPct}%</strong>.</>
              )}
              {strategySummaryContent.hasDelay && (
                <> Je stelt de aflossing <strong>{strategySummaryContent.delayYears} jaar</strong> uit.</>
              )}
              {strategySummaryContent.hasInvestment && (
                <>
                  {strategySummaryContent.investment > 0 ? (
                    <> Daarnaast investeer je <strong>€{strategySummaryContent.investment.toLocaleString("nl-NL")} per maand</strong>.</>
                  ) : (
                    <> Je houdt rekening met <strong>belegd startkapitaal</strong>.</>
                  )}
                  {strategySummaryContent.hasInvestmentGrowth && <> Ook je inleg laat je <strong>jaarlijks met {strategySummaryContent.investmentGrowthPct}%</strong> stijgen.</>}
                </>
              )}
            </p>
            <p style={{ marginBottom: 0 }}>
              <strong>Wat is de uitkomst?</strong>
              {strategySummaryContent.isNotPaidOff ? (
                <> Binnen de looptijd van <strong>{strategySummaryContent.totalTermYears} jaar</strong> wordt de lening <strong>niet</strong> afgelost.</>
              ) : (
                <>
                  De lening is afgelost in <strong>{strategySummaryContent.payoffRemainderMonths > 0 ? `${strategySummaryContent.payoffYears} jaar en ${strategySummaryContent.payoffRemainderMonths} maanden` : `${strategySummaryContent.payoffYears} jaar`}</strong>.
                  Je betaalt in totaal <strong>€{strategySummaryContent.interestTotal.toLocaleString("nl-NL")}</strong> aan rente.
                  {strategySummaryContent.hasInvestment && strategySummaryContent.finalInv > 0 && (
                    <> De waarde van je investeringen aan het eind bedraagt <strong>€{strategySummaryContent.finalInv.toLocaleString("nl-NL")}</strong>.</>
                  )}
                </>
              )}
            </p>
          </div>

          <h2 style={{ fontSize: 18, fontWeight: 700, color: "#1f2937", marginTop: 24, marginBottom: 12 }}>Aflosschema</h2>
          <div style={{ overflowX: "auto", marginBottom: 12, WebkitOverflowScrolling: "touch" }}>
            <table style={{ width: "100%", minWidth: 520, borderCollapse: "collapse", fontSize: 12 }}>
              <thead>
                <tr>
                  <th style={{ textAlign: "left", padding: "10px 8px", background: "#3399FF", color: "#fff", fontWeight: 600, whiteSpace: "normal", lineHeight: 1.3, maxWidth: 60, position: "sticky", left: 0, zIndex: 2 }}>Jaar</th>
                  <th style={{ textAlign: "right", padding: "10px 8px", background: "#3399FF", color: "#fff", fontWeight: 600, whiteSpace: "normal", lineHeight: 1.3, maxWidth: 90 }}>Maandelijkse aflossing</th>
                  <th style={{ textAlign: "right", padding: "10px 8px", background: "#3399FF", color: "#fff", fontWeight: 600, whiteSpace: "normal", lineHeight: 1.3, maxWidth: 90 }}>Maandelijkse investering</th>
                  <th style={{ textAlign: "right", padding: "10px 8px", background: "#3399FF", color: "#fff", fontWeight: 600, whiteSpace: "normal", lineHeight: 1.3, maxWidth: 90 }}>Eenmalige aflossingen</th>
                  <th style={{ textAlign: "right", padding: "10px 8px", background: "#3399FF", color: "#fff", fontWeight: 600, whiteSpace: "normal", lineHeight: 1.3, maxWidth: 90 }}>Waarde investeringen (eind)</th>
                  <th style={{ textAlign: "right", padding: "10px 8px", background: "#3399FF", color: "#fff", fontWeight: 600, whiteSpace: "normal", lineHeight: 1.3, maxWidth: 90 }}>Totaal betaalde rente</th>
                  <th style={{ textAlign: "right", padding: "10px 8px", background: "#3399FF", color: "#fff", fontWeight: 600, whiteSpace: "normal", lineHeight: 1.3, maxWidth: 90 }}>Restant schuld</th>
                </tr>
              </thead>
              <tbody>
                {yearlyTableData.map((row) => (
                  <tr key={row.year} style={{ borderBottom: "1px solid #f3f4f6" }}>
                    <td style={{ padding: "8px", color: "#1f2937", fontWeight: 500, position: "sticky", left: 0, zIndex: 1, background: "#fff", boxShadow: "2px 0 4px rgba(0,0,0,0.06)" }}>{row.year + 1}</td>
                    <td style={{ padding: "8px", textAlign: "right", color: "#374151" }}>€{(row.monthlyPayment ?? 0).toLocaleString("nl-NL", { maximumFractionDigits: 0 })}</td>
                    <td style={{ padding: "8px", textAlign: "right", color: "#374151" }}>€{(row.monthlyInvestment ?? 0).toLocaleString("nl-NL", { maximumFractionDigits: 0 })}</td>
                    <td style={{ padding: "8px", textAlign: "right", color: "#374151" }}>€{(row.oneTimeThisYear ?? 0).toLocaleString("nl-NL", { maximumFractionDigits: 0 })}</td>
                    <td style={{ padding: "8px", textAlign: "right", color: "#374151" }}>€{(row.investmentValue ?? 0).toLocaleString("nl-NL", { maximumFractionDigits: 0 })}</td>
                    <td style={{ padding: "8px", textAlign: "right", color: "#374151" }}>€{(row.interestPaid ?? 0).toLocaleString("nl-NL", { maximumFractionDigits: 0 })}</td>
                    <td style={{ padding: "8px", textAlign: "right", color: "#374151" }}>€{(row.balance ?? 0).toLocaleString("nl-NL", { maximumFractionDigits: 0 })}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p style={{ fontSize: 13, color: "#6b7280", lineHeight: 1.5, marginTop: 0, marginBottom: 20 }}>
            <strong>Betaalschema</strong>: laat het verloop van je maandelijkse betaling zien (incl. jaarlijkse stijging als je die inschakelt). De lijn stopt zodra de lening is afbetaald.
          </p>
        </div>
      )}

      {mainView === "portal" && (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            flex: "1 1 0",
            minHeight: 0,
            overflow: "hidden",
            overscrollBehavior: "none",
          }}
          onWheel={handlePortalWheel}
          onTouchStart={handlePortalTouchStart}
          onTouchMove={handlePortalTouchMove}
          onTouchEnd={handlePortalTouchEnd}
          onTouchCancel={handlePortalTouchEnd}
        >
          {/* Vast bovenblok: modus-knoppen + tegels of grafiek scrollen niet mee; onderste paneel scrollt apart. */}
          <div
            style={{
              flex: "1 1 0",
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
              background: "#fff",
              overflow: "hidden",
              paddingTop: MOBILE_BLOCK_GAP,
            }}
          >
            <div ref={tutorialModusTilesRef} style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", overflow: "hidden" }}>
            <div style={topToggle}>
              <button style={toggleBtn(viewMode === "summary")} onClick={() => setViewMode("summary")}>
                Summary Modus
              </button>
              <button
                ref={tutorialChartBtnRef}
                type="button"
                className={tutorialPulseChartBtn ? "tutorial-target-pulse" : undefined}
                style={toggleBtn(viewMode === "chart")}
                onClick={() => setViewMode("chart")}
              >
                Chart Modus
              </button>
            </div>

            {viewMode === "summary" && (
              <div style={summaryGridPortal}>
                {TILE_IDS.slice(0, 2).map(renderPortalSummaryTile)}
                <div
                  style={{
                    gridColumn: "1 / -1",
                    gridRow: "2 / span 2",
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gridTemplateRows: "repeat(2, minmax(0, 1fr))",
                    gap: 10,
                    position: "relative",
                    minHeight: 0,
                    alignContent: "stretch",
                  }}
                >
                  {TILE_IDS.slice(2).map(renderPortalSummaryTile)}
                </div>
              </div>
            )}

            {viewMode === "chart" && (
              <div
                /* Voorkomt dat touch op de grafiek (hover) naar bottomScrollRef wordt omgezet */
                data-no-scroll-proxy="true"
                style={{
                  flex: 1,
                  minHeight: 0,
                  display: "flex",
                  flexDirection: "column",
                  padding: `${MOBILE_BLOCK_GAP}px 4px 8px`,
                  width: "100%",
                  boxSizing: "border-box",
                  minWidth: 0,
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    flexShrink: 0,
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    marginBottom: 8,
                    marginLeft: 8,
                    marginTop: 0,
                    flexWrap: "wrap",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <div style={{ width: 16, height: 3, backgroundColor: "#3b5bfd", borderRadius: 2 }} />
                    <span style={{ fontSize: 12, color: "#374151" }}>Openstaande schuld</span>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <div style={{ width: 16, height: 3, backgroundColor: "#94a3b8", borderRadius: 2 }} />
                    <span style={{ fontSize: 12, color: "#374151" }}>Betaalde rente</span>
                  </div>
                  {calc.enableInvestment && (
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <div style={{ width: 16, height: 3, backgroundColor: "#10b981", borderRadius: 2 }} />
                      <span style={{ fontSize: 12, color: "#374151" }}>Waarde investeringen</span>
                    </div>
                  )}
                </div>
                <div
                  ref={chartAreaRef}
                  style={{
                    flex: 1,
                    minHeight: 0,
                    minWidth: 0,
                    width: "100%",
                    overflow: "hidden",
                  }}
                >
                  <PortalChart
                    data={calc.chartDataMonthly}
                    hoverYear={sharedHoverYear}
                    setHoverYear={setSharedHoverYear}
                    enableInvestment={calc.enableInvestment}
                    payoffByInvestment={calc.payoffByInvestment}
                    payoffMonths={calc.payoffMonths}
                    enableDelay={calc.enableDelay}
                    delayYears={calc.delayYears}
                    delayMonths={calc.delayMonths}
                    totalTermYears={totalTermYears}
                    totalTermMonths={calc.delayMonths}
                    planYears={calc.effectivePlanYears}
                    chartHeight={chartFillHeight}
                    fontSize={mobileChartFontSize}
                    showPayoffLabelInChart={false}
                    lineStrokeWidth={3}
                  />
                </div>
                {showInvestmentPayoffBadge && (
                  <div
                    style={{
                      flexShrink: 0,
                      marginTop: 8,
                      marginLeft: 8,
                      marginRight: 8,
                      marginBottom: 4,
                      padding: "8px 10px",
                      background: "#EFF6FF",
                      border: "1px solid #3b5bfd",
                      borderRadius: 10,
                      color: "#3b5bfd",
                      fontSize: 12,
                      fontWeight: 500,
                      textAlign: "center",
                    }}
                  >
                    Lening afbetaald via investering
                  </div>
                )}
              </div>
            )}
            </div>
          </div>

          <div
            ref={bottomScrollRef}
            style={{
              ...bottomPane,
              height: Math.min(Math.round(viewportHeight * 0.44), 400),
            }}
          >
        <style>{BOX_SLIDER_GLOBAL_STYLE}</style>
        <div style={categoryPaneHeaderRow}>
          <span style={categoryPaneTitle}>
            {MENU_OPTIONS.find((o) => o.id === selectedBottomPane)?.label ?? "Invoer"}
          </span>
          <button
            ref={tutorialCategoryBtnRef}
            type="button"
            className={tutorialPulseCategoryBtn ? "tutorial-target-pulse" : undefined}
            style={categoryButtonNarrow}
            onClick={openBottomCategoryMenu}
          >
            Kies categorie
          </button>
        </div>
        {selectedBottomPane === "basisinformatie" && (
          <div style={bottomRow}>
            <div style={{ display: "flex", alignItems: "center", marginBottom: 8 }}>
              <span style={bottomLabelInline}>Studieschuld</span>
              <MobileInfo text="Voer hier het totaalbedrag van je studieschuld in. Dit is het bedrag dat je nog moet terugbetalen aan DUO (inclusief eventuele rente die al is bijgeschreven)." />
              {isDebtLocked && (
                <MobileInfo text="Je studieschuld staat vast en kan niet meer worden gewijzigd. Heb je vragen of staat er iets fout? Mail naar info@slimmeraf.nl." />
              )}
            </div>
            <input
              type="number"
              min={0}
              step={100}
              placeholder="Bijv. 23000"
              value={calc.totalDebt === 0 ? "" : calc.totalDebt}
              disabled={isDebtLocked}
              readOnly={isDebtLocked}
              onChange={(e) => {
                if (isDebtLocked) return;
                const v = e.target.value;
                calc.setTotalDebt(v === "" ? 0 : Number(v));
              }}
              style={{
                width: "100%",
                padding: "10px 12px",
                borderRadius: 8,
                border: "1px solid rgba(255,255,255,0.3)",
                background: "rgba(255,255,255,0.1)",
                color: "#fff",
                fontSize: 14,
                marginBottom: 12,
                boxSizing: "border-box",
                opacity: isDebtLocked ? 0.8 : 1,
                cursor: isDebtLocked ? "not-allowed" : "text",
              }}
            />
            <div ref={setWegwijzerRef("stelsel")}>
            <div data-wegwijzer-title style={{ display: "flex", alignItems: "center", marginBottom: 8 }}>
              <span style={bottomLabelInline}>Stelsel (aflostermijn)</span>
              <MobileInfo text="Het stelsel bepaalt in hoeveel jaar je je studieschuld moet aflossen: 15 jaar of 35 jaar. Het stelsel hangt af van wanneer je je eerste studiefinanciering ontving. Bij 35 jaar is je maandbedrag lager, maar betaal je over het algemeen meer rente." />
            </div>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              <button
                type="button"
                style={{
                  ...activeToggleBase,
                  ...(calc.repaymentPlan === "15" ? activeToggleActive : activeToggleInactive),
                }}
                onClick={() => calc.setRepaymentPlan("15")}
              >
                15 jaar
              </button>
              <button
                type="button"
                style={{
                  ...activeToggleBase,
                  ...(calc.repaymentPlan === "35" ? activeToggleActive : activeToggleInactive),
                }}
                onClick={() => calc.setRepaymentPlan("35")}
              >
                35 jaar
              </button>
            </div>
            </div>
            <div ref={setWegwijzerRef("alBegonnen")}>
            <div data-wegwijzer-title style={{ display: "flex", alignItems: "center", marginTop: 16, marginBottom: 8 }}>
              <span style={bottomLabelInline}>Al begonnen met aflossen?</span>
              <MobileInfo text="Heb je al eerder aflossingen gedaan? Als je jaarlijks aflost, vul hier in hoeveel jaar je al bezig bent. De resterende termijn wordt dan berekend (bijv. 35 jaar stelsel - 2 jaar = 33 jaar resterend)." />
            </div>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 12 }}>
              <button
                type="button"
                style={{
                  ...activeToggleBase,
                  ...(!calc.hasStartedRepaying ? activeToggleActive : activeToggleInactive),
                }}
                onClick={() => calc.setHasStartedRepaying(false)}
              >
                Nee
              </button>
              <button
                type="button"
                style={{
                  ...activeToggleBase,
                  ...(calc.hasStartedRepaying ? activeToggleActive : activeToggleInactive),
                }}
                onClick={() => calc.setHasStartedRepaying(true)}
              >
                Ja
              </button>
            </div>
            </div>
            {calc.hasStartedRepaying && (
              <div ref={setWegwijzerRef("jaarAlBezig")}>
                <div data-wegwijzer-title style={{ display: "flex", alignItems: "center", marginBottom: 8 }}>
                  <span style={bottomLabel}>Hoeveel jaar ben je al bezig met aflossen?</span>
                </div>
                <input
                  type="number"
                  min={0}
                  max={Math.max(0, calc.planYears - 1)}
                  step={1}
                  placeholder={`0 t/m ${calc.planYears - 1}`}
                  value={calc.yearsAlreadyRepaid === 0 ? "" : calc.yearsAlreadyRepaid}
                  onChange={(e) => {
                    const v = e.target.value;
                    const n = v === "" ? 0 : Math.min(Math.max(0, Number(v)), calc.planYears - 1);
                    calc.setYearsAlreadyRepaid(n);
                  }}
                  style={{
                    width: "100%",
                    padding: "10px 12px",
                    borderRadius: 8,
                    border: "1px solid rgba(255,255,255,0.3)",
                    background: "rgba(255,255,255,0.1)",
                    color: "#fff",
                    fontSize: 14,
                    marginBottom: 12,
                    boxSizing: "border-box",
                  }}
                />
                <div style={{ fontSize: 12, color: "rgba(255,255,255,0.8)", marginBottom: 12 }}>
                  Resterende termijn: {calc.effectivePlanYears} jaar
                </div>
              </div>
            )}
            {/* Zelf rente instellen */}
            <div ref={setWegwijzerRef("zelfRente")} style={{ marginTop: 20, marginBottom: 16 }}>
              <div data-wegwijzer-title style={{ display: "flex", alignItems: "center", marginBottom: 8 }}>
                <span style={bottomLabelInline}>Zelf rente instellen</span>
                <MobileInfo text="Standaard wordt de DUO-rente van je aflosstelsel gebruikt (2,21% voor 15 jaar, 2,57% voor 35 jaar). Voor sommige situaties kan het percentage net afwijken; dat kun je hier invullen. Met deze optie kun je ook per periode een ander rentepercentage invoeren." />
              </div>
              <button
              type="button"
              style={calc.enableCustomRates ? activeToggleActive : activeToggleInactive}
              onClick={() => {
                const next = !calc.enableCustomRates;
                calc.setEnableCustomRates(next);
                if (next && !calc.customRates?.length) calc.setCustomRates([{ year: 0, ratePct: 0 }]);
              }}
            >
              {calc.enableCustomRates ? "Actief" : "Inactief"}
            </button>
              {!calc.enableCustomRates && (
                <p style={{ margin: 0, fontSize: 13, color: "rgba(255,255,255,0.85)", lineHeight: 1.5 }}>
                  Er wordt gebruikgemaakt van het rentepercentage van je aflosstelsel: {calc.planYears === 15 ? "2,21%" : "2,57%"} ({calc.planYears} jaar).
                </p>
              )}
              {calc.enableCustomRates && (() => {
                const segments = [...(calc.customRates || [])].filter((r) => r != null && typeof r.year === "number").sort((a, b) => a.year - b.year);
                const usedYears = new Set(segments.map((r) => r.year));
                const totalTermYears = calc.effectivePlanYears + (calc.enableDelay ? calc.delayYears : 0);
                const maxYear = Math.max(0, totalTermYears - 1);
                const list = segments.length === 0 ? [{ year: 0, ratePct: 0 }] : segments;
                return (
                  <>
                    {list.map((seg, idx) => (
                      <div key={idx} style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 8, flexWrap: "wrap" }}>
                        {seg.year === 0 ? (
                          <span style={{ fontSize: 14, color: "rgba(255,255,255,0.95)", flex: "1 1 140px" }}>Vanaf jaar 0</span>
                        ) : (
                          <select
                            value={seg.year}
                            onChange={(e) => {
                              const newY = Math.max(1, Math.min(maxYear, Number(e.target.value)));
                              const otherYears = new Set(segments.filter((s, i) => i !== idx).map((s) => s.year));
                              if (otherYears.has(newY)) return;
                              calc.setCustomRates((prev) => prev.map((s, i) => (i === idx ? { ...s, year: newY } : s)).sort((a, b) => a.year - b.year));
                            }}
                            style={{
                              flex: "1 1 140px",
                              minWidth: 120,
                              padding: "8px 10px",
                              borderRadius: 8,
                              border: "1px solid rgba(255,255,255,0.3)",
                              background: "rgba(255,255,255,0.1)",
                              color: "#fff",
                              fontSize: 14,
                            }}
                          >
                            {Array.from({ length: maxYear + 1 }, (_, i) => i).filter((y) => y >= 1 && (y === seg.year || !segments.some((s, i) => i !== idx && s.year === y))).map((y) => (
                              <option key={y} value={y} style={{ background: "#1f2937", color: "#fff" }}>Vanaf jaar {y}</option>
                            ))}
                          </select>
                        )}
                        <span style={{ display: "flex", alignItems: "center", gap: 4, marginLeft: "auto" }}>
                          <input
                            type="number"
                            min={0}
                            max={20}
                            step={0.01}
                            value={seg.ratePct === 0 ? "" : seg.ratePct}
                            onChange={(e) => {
                              const v = e.target.value;
                              const pct = v === "" ? 0 : Math.max(0, Math.min(20, Number(v)));
                              calc.setCustomRates((prev) => {
                                const next = prev.map((s, i) => (i === idx ? { ...s, ratePct: pct } : s));
                                return next.length ? next : [{ year: 0, ratePct: pct }];
                              });
                            }}
                            style={{
                              width: 70,
                              flexShrink: 0,
                              padding: "8px 10px",
                              borderRadius: 8,
                              border: "1px solid rgba(255,255,255,0.3)",
                              background: "rgba(255,255,255,0.1)",
                              color: "#fff",
                              fontSize: 14,
                            }}
                          />
                          <span style={{ color: "rgba(255,255,255,0.95)", fontSize: 14 }}>%</span>
                        </span>
                      </div>
                    ))}
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 6 }}>
                      <button
                        type="button"
                        onClick={() => {
                          const nextYear = Array.from({ length: maxYear + 1 }, (_, i) => i).find((y) => !usedYears.has(y));
                          if (nextYear !== undefined) calc.setCustomRates((prev) => [...prev, { year: nextYear, ratePct: 0 }].sort((a, b) => a.year - b.year));
                        }}
                        style={{ display: "flex", alignItems: "center", justifyContent: "center", width: 40, height: 40, borderRadius: 10, border: "1px solid rgba(255,255,255,0.4)", background: "rgba(255,255,255,0.15)", color: "#fff", fontSize: 20, cursor: "pointer" }}
                      >
                        +
                      </button>
                      <span style={{ fontSize: 13, color: "rgba(255,255,255,0.85)" }}>Renteperiode toevoegen</span>
                    </div>
                  </>
                );
              })()}
            </div>
          </div>
        )}
        {selectedBottomPane === "aflossingen" && (
          <div style={bottomRow}>
            <div ref={setWegwijzerRef("minDuo")}>
            <div data-wegwijzer-title style={{ display: "flex", alignItems: "center", marginBottom: 8 }}>
              <span style={bottomLabelInline}>Minimale aflossing DUO</span>
              <MobileInfo text="De minimale aflossing wordt berekend als 4% van het verschil tussen je bruto jaarloon en de draagkrachtvrije voet (€26.136 in 2026). Formule: (Bruto jaarloon - €26.136) × 4% / 12 maanden. Je kunt je minimale aflossing ook vinden in Mijn DUO." />
            </div>
            <input
              type="number"
              min={0}
              step={1}
              placeholder="Bijv. 120"
              value={calc.minPayment === 0 ? "" : calc.minPayment}
              onChange={(e) => {
                const v = e.target.value;
                calc.setMinPayment(v === "" ? 0 : Number(v));
              }}
              style={{
                width: "100%",
                padding: "10px 12px",
                borderRadius: 8,
                border: "1px solid rgba(255,255,255,0.3)",
                background: "rgba(255,255,255,0.1)",
                color: "#fff",
                fontSize: 14,
                marginBottom: 12,
                boxSizing: "border-box",
              }}
            />
            </div>
            <div ref={setWegwijzerRef("maandelijkseAflossing")}>
            <div data-wegwijzer-title style={{ display: "flex", alignItems: "center", marginBottom: 8 }}>
              <span style={bottomLabelInline}>Maandelijkse aflossing</span>
              <MobileInfo text="Het totaalbedrag dat je per maand op je studieschuld aflost in dit scenario. Dat is je maandelijkse aflossing zoals de portal die toepast — en die zit altijd bovenop (en is dus inclusief) de minimale aflossing DUO die je hierboven hebt ingevuld. Je kunt niet lager gaan dan dat wettelijke minimum." />
            </div>
            <BoxSlider
              key="monthly-payment"
              min={Math.max(monthlyMin, calc.effectiveMinPayment)}
              max={monthlyMax}
              step={5}
              value={Math.min(monthlyMax, Math.max(calc.effectiveMinPayment, calc.monthlyPayment))}
              onChange={(v) => {
                setMonthlyPaymentInput("");
                calc.setMonthlyPayment(v);
              }}
            />
            <div style={{ fontSize: 12, color: "rgba(255,255,255,0.7)", marginTop: 4, marginBottom: 8 }}>
              € {calc.constrainedMonthlyPayment} / maand (min. €{calc.effectiveMinPayment})
            </div>
            <input
              type="number"
              min={calc.effectiveMinPayment}
              max={monthlyMax}
              step={5}
              placeholder={`Typ bedrag (min. €${calc.effectiveMinPayment})`}
              value={monthlyPaymentInput}
              onChange={(e) => {
                const v = e.target.value;
                setMonthlyPaymentInput(v);
                if (v === "") {
                  calc.setMonthlyPayment(calc.effectiveMinPayment);
                  return;
                }
                const n = Number(v);
                if (!Number.isNaN(n)) {
                  calc.setMonthlyPayment(Math.min(monthlyMax, Math.max(calc.effectiveMinPayment, Math.round(n))));
                }
              }}
              style={{
                width: "100%",
                padding: "10px 12px",
                borderRadius: 8,
                border: "1px solid rgba(255,255,255,0.3)",
                background: "rgba(255,255,255,0.1)",
                color: "#fff",
                fontSize: 14,
                boxSizing: "border-box",
              }}
            />
            </div>
            <div ref={setWegwijzerRef("jaarlijkseStijgingAflossing")}>
            <div data-wegwijzer-title style={{ display: "flex", alignItems: "center", marginTop: 16, marginBottom: 8 }}>
              <span style={bottomLabelInline}>Jaarlijkse stijging maandbedrag</span>
              <MobileInfo text="Laat je maandelijkse aflossing jaarlijks met een vast percentage stijgen (bijv. door salarisgroei). Het percentage wordt elk jaar op het bedrag van het jaar ervoor toegepast." />
            </div>
            <button
              type="button"
              style={calc.enableGrowth ? activeToggleActive : activeToggleInactive}
              onClick={() => calc.setEnableGrowth(!calc.enableGrowth)}
            >
              {calc.enableGrowth ? "Actief" : "Inactief"}
            </button>
            {calc.enableGrowth && (
              <>
                <BoxSlider
                  key="growth-rate"
                  min={0}
                  max={PORTAL_GROWTH_SLIDER_MAX}
                  step={1}
                  value={Math.min(PORTAL_GROWTH_SLIDER_MAX, Math.max(0, calc.growthRate))}
                  onChange={(v) => calc.setGrowthRate(v)}
                />
                <div style={{ fontSize: 12, color: "rgba(255,255,255,0.7)", marginTop: 4 }}>
                  {(calc.growthRate / 10).toFixed(1)}% per jaar (0% – 5%)
                </div>
              </>
            )}
            </div>
          </div>
        )}
        {selectedBottomPane === "uitstellen" && (
          <div style={bottomRow}>
            <div style={{ display: "flex", alignItems: "center", marginBottom: 8 }}>
              <span style={bottomLabelInline}>Aflossingen uitstellen</span>
              <MobileInfo text="Je mag maximaal 5 jaar je aflossing uitstellen. Zet het aantal jaren op 0 als je geen uitstel wilt. Tijdens uitstel loopt de rente wel door en wordt die bij je schuld opgeteld. Voor meer informatie over uitstel en de voorwaarden kun je terecht op de website van DUO." />
            </div>
            <div ref={setWegwijzerRef("delayYears")}>
              <div data-wegwijzer-title style={{ ...bottomLabel, marginTop: 0 }}>Jaren uitstel</div>
              <BoxSlider
                key="delay-years"
                min={0}
                max={5}
                step={1}
                value={Math.min(5, Math.max(0, calc.delayYears))}
                onChange={(v) => calc.setDelayYears(v)}
              />
              <div style={{ fontSize: 12, color: "rgba(255,255,255,0.7)", marginTop: 4 }}>
                {calc.delayYears} jaar
              </div>
            </div>
          </div>
        )}
        {selectedBottomPane === "investeren" && (
          <div style={bottomRow}>
            <div style={{ display: "flex", alignItems: "center", marginBottom: 8 }}>
              <span style={bottomLabelInline}>Investeren naast aflossen</span>
              <MobileInfo text="Je kunt naast je maandelijkse aflossing ook geld investeren. Zet maandbedrag en eventueel startkapitaal op 0 als je niet wilt beleggen. De investering groeit mee (geschat 6% per jaar) en kan je resterende schuld overtreffen, waarna je in één keer kunt afbetalen." />
            </div>
            <div ref={setWegwijzerRef("maandelijkseInleg")}>
              <div data-wegwijzer-title style={{ ...bottomLabel, marginTop: 0 }}>Maandelijkse inleg</div>
              <BoxSlider
                key="monthly-investment"
                min={0}
                max={invMax}
                step={10}
                value={Math.min(invMax, Math.max(0, calc.monthlyInvestment || 0))}
                onChange={(v) => calc.setMonthlyInvestment(v)}
              />
              <div style={{ fontSize: 12, color: "rgba(255,255,255,0.7)", marginTop: 4 }}>
                € {calc.monthlyInvestment || 0} / maand
              </div>
            </div>
            <div ref={setWegwijzerRef("startkapitaal")}>
              <div data-wegwijzer-title style={{ display: "flex", alignItems: "center", marginTop: 16, marginBottom: 8 }}>
                <span style={bottomLabelInline}>Startkapitaal investeringen (optioneel)</span>
                <MobileInfo text="Heb je al geld belegd in aandelen, indexfondsen of andere investeringen? Voer hier het totale bedrag in dat je nu al hebt staan. Dit startbedrag groeit mee met de investeringen die je maandelijks doet, waardoor je sneller je lening kunt afbetalen. Als je nog geen investeringen hebt, laat je dit veld leeg." />
              </div>
              <input
                type="number"
                min={0}
                step={100}
                placeholder="Bijv. 5000"
                value={calc.startingInvestmentCapital === 0 ? "" : calc.startingInvestmentCapital}
                onChange={(e) => {
                  const v = e.target.value;
                  calc.setStartingInvestmentCapital(v === "" ? 0 : Number(v));
                }}
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  borderRadius: 8,
                  border: "1px solid rgba(255,255,255,0.3)",
                  background: "rgba(255,255,255,0.1)",
                  color: "#fff",
                  fontSize: 14,
                  boxSizing: "border-box",
                }}
              />
            </div>
            <div ref={setWegwijzerRef("investGroei")}>
              <div data-wegwijzer-title style={{ display: "flex", alignItems: "center", marginTop: 16, marginBottom: 8 }}>
                <span style={bottomLabelInline}>Jaarlijkse stijging investeringen</span>
                <MobileInfo text="Laat je maandelijkse inleg jaarlijks met een vast percentage stijgen. Je kunt dit onafhankelijk van de stijging van je aflossingen instellen." />
              </div>
              <button
                type="button"
                style={calc.enableSeparateGrowth ? activeToggleActive : activeToggleInactive}
                onClick={() => calc.setEnableSeparateGrowth(!calc.enableSeparateGrowth)}
              >
                {calc.enableSeparateGrowth ? "Actief" : "Inactief"}
              </button>
              {calc.enableSeparateGrowth && (
                <>
                  <BoxSlider
                    key="investment-growth-rate"
                    min={0}
                    max={PORTAL_GROWTH_SLIDER_MAX}
                    step={1}
                    value={Math.min(PORTAL_GROWTH_SLIDER_MAX, Math.max(0, calc.investmentGrowthRate))}
                    onChange={(v) => calc.setInvestmentGrowthRate(v)}
                  />
                  <div style={{ fontSize: 12, color: "rgba(255,255,255,0.7)", marginTop: 4 }}>
                    {(calc.investmentGrowthRate / 10).toFixed(1)}% per jaar (0% – 5%)
                  </div>
                </>
              )}
            </div>
          </div>
        )}
        {selectedBottomPane === "eenmalig" && (
          <div ref={setWegwijzerRef("eenmalig")} style={bottomRow}>
            <div data-wegwijzer-title style={{ display: "flex", alignItems: "center", marginBottom: 12 }}>
              <span style={bottomLabelInline}>Eenmalige aflossingen</span>
              <MobileInfo text="Voeg eenmalige aflossingen toe die je in één keer op een bepaald moment doet. Bijvoorbeeld een erfenis of bonus die je wilt inzetten om je studieschuld te verlagen." />
            </div>
            {calc.oneTimeRepayments.map((r, idx) => (
              <div key={idx} style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 12 }}>
                <input
                  type="number"
                  min={0}
                  step={100}
                  placeholder="Bedrag"
                  value={r.amount === 0 ? "" : r.amount}
                  onChange={(e) => {
                    const v = e.target.value;
                    const amt = v === "" ? 0 : Math.max(0, Number(v));
                    calc.setOneTimeRepayments((prev) =>
                      prev.map((x, i) => (i === idx ? { ...x, amount: amt } : x))
                    );
                  }}
                  style={{
                    flex: 1,
                    padding: "10px 12px",
                    borderRadius: 8,
                    border: "1px solid rgba(255,255,255,0.3)",
                    background: "rgba(255,255,255,0.1)",
                    color: "#fff",
                    fontSize: 14,
                    boxSizing: "border-box",
                  }}
                />
                <select
                  value={r.year || 1}
                  onChange={(e) => {
                    const yr = Math.max(1, Math.min(totalTermYears, Number(e.target.value)));
                    calc.setOneTimeRepayments((prev) =>
                      prev.map((x, i) => (i === idx ? { ...x, year: yr } : x))
                    );
                  }}
                  style={{
                    padding: "10px 12px",
                    borderRadius: 8,
                    border: "1px solid rgba(255,255,255,0.3)",
                    background: "rgba(255,255,255,0.1)",
                    color: "#fff",
                    fontSize: 14,
                    minWidth: 90,
                  }}
                >
                  {Array.from({ length: Math.max(1, totalTermYears) }, (_, i) => i + 1).map((y) => (
                    <option key={y} value={y} style={{ background: "#374151", color: "#fff" }}>
                      Jaar {y}
                    </option>
                  ))}
                </select>
              </div>
            ))}
            <button
              type="button"
              onClick={() =>
                calc.setOneTimeRepayments((prev) => [...prev, { amount: 0, year: 1 }])
              }
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: 40,
                height: 40,
                borderRadius: 20,
                border: "2px solid rgba(255,255,255,0.5)",
                background: "rgba(255,255,255,0.1)",
                color: "#fff",
                fontSize: 24,
                fontWeight: 300,
                cursor: "pointer",
                padding: 0,
                lineHeight: 1,
              }}
              aria-label="Nog een eenmalige aflossing toevoegen"
            >
              +
            </button>
          </div>
        )}
      </div>

      {menuOpen && (
        <div style={menuOverlay} onClick={closeCategoryMenuOrSkipTutorial}>
          <div ref={categoryMenuSheetRef} style={menuSheet} onClick={(e) => e.stopPropagation()}>
            <div style={menuTitle}>Kies wat je onderin wilt bewerken</div>
            {MENU_OPTIONS.map((opt) => (
              <div
                key={opt.id}
                style={menuItem(selectedBottomPane === opt.id)}
                onClick={() => selectMenuOption(opt.id)}
              >
                <span style={{ fontWeight: 500, color: "#1f2937" }}>{opt.label}</span>
                {selectedBottomPane === opt.id && (
                  <span style={{ color: "#3399FF", fontWeight: 600 }}>✓</span>
                )}
              </div>
            ))}
            <button
              type="button"
              style={{ ...menuButton, marginTop: 16, marginLeft: 0, marginRight: 0, marginBottom: 0, width: "100%" }}
              onClick={closeCategoryMenuOrSkipTutorial}
            >
              Sluiten
            </button>
          </div>
        </div>
      )}
        </div>
      )}

      {headerMenuOpen && (
        <div
          style={{ ...menuOverlay, alignItems: "flex-end", justifyContent: "center" }}
          onClick={() => setHeaderMenuOpen(false)}
        >
          <div
            style={{ ...menuSheet, maxHeight: "50vh", display: "flex", flexDirection: "column" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ ...menuTitle, marginBottom: 16 }}>Menu</div>
            {HEADER_MENU_ITEMS.map((item) => (
              <div
                key={item.id}
                style={menuItem(false)}
                onClick={() => {
                  if (item.id === "portal") {
                    setMainView("portal");
                    setHeaderMenuOpen(false);
                  } else if (item.id === "saved") {
                    setMainView("saved");
                    setHeaderMenuOpen(false);
                  } else if (item.id === "faq") {
                    setMainView("faq");
                    setHeaderMenuOpen(false);
                  }
                }}
              >
                <span style={{ fontWeight: 500, color: "#1f2937" }}>{item.label}</span>
              </div>
            ))}
            <div
              style={{
                display: "flex",
                gap: 10,
                marginTop: 12,
                width: "100%",
              }}
            >
              <button
                type="button"
                onClick={restartMobilePortalTutorial}
                style={{
                  flex: 1,
                  minWidth: 0,
                  padding: "14px 12px",
                  borderRadius: 12,
                  border: "1px solid #bfdbfe",
                  background: "rgba(51,153,255,0.08)",
                  color: "#1d4ed8",
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: "pointer",
                  textAlign: "center",
                  fontFamily: "inherit",
                }}
              >
                Restart tutorial
              </button>
              <button
                type="button"
                onClick={startWegwijzerFromMenu}
                style={{
                  flex: 1,
                  minWidth: 0,
                  padding: "14px 12px",
                  borderRadius: 12,
                  border: "1px solid #bfdbfe",
                  background: "rgba(51,153,255,0.08)",
                  color: "#1d4ed8",
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: "pointer",
                  textAlign: "center",
                  fontFamily: "inherit",
                }}
              >
                Wegwijzer
              </button>
            </div>
            <div style={{ marginTop: "auto", paddingTop: 20, textAlign: "left" }}>
              <button
                type="button"
                onClick={() => {
                  setHeaderMenuOpen(false);
                  logout().then(() => window.location.assign("/"));
                }}
                style={{
                  background: "none",
                  border: "none",
                  padding: 0,
                  color: "#6b7280",
                  fontSize: 14,
                  cursor: "pointer",
                }}
              >
                Uitloggen
              </button>
            </div>
          </div>
        </div>
      )}

      {mobilePortalTutorialOverlayProps && (
        <MobilePortalTutorialOverlay
          {...mobilePortalTutorialOverlayProps}
          onNext={tutorialOnNext}
          onPrevious={tutorialOnPrevious}
        />
      )}
      {isDemoMode && (
        <div
          aria-hidden
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 10150,
            background: "transparent",
            pointerEvents: "auto",
          }}
        />
      )}
      {!isDemoMode && !debtLockLoading && debtLockRequired && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 10300,
            backdropFilter: "blur(12px)",
            WebkitBackdropFilter: "blur(12px)",
            background: "rgba(255, 255, 255, 0.45)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 16,
            boxSizing: "border-box",
          }}
        >
          <div
            style={{
              width: "100%",
              maxWidth: 460,
              background: "#ffffff",
              borderRadius: 16,
              border: "1px solid #e5e7eb",
              boxShadow: "0 18px 48px rgba(0,0,0,0.2)",
              padding: "18px 18px 16px",
              boxSizing: "border-box",
            }}
          >
            <h2 style={{ margin: "0 0 10px", fontSize: 20, color: "#111827" }}>Bevestig je studieschuld</h2>
            <p style={{ margin: "0 0 10px", color: "#4b5563", fontSize: 14, lineHeight: 1.55 }}>
              Vul hieronder je totale studieschuld in voordat je de portal gebruikt.
              Deze waarde wordt daarna vastgezet en kun je niet meer wijzigen.
            </p>
            <p style={{ margin: "0 0 14px", color: "#4b5563", fontSize: 14, lineHeight: 1.55 }}>
              We slaan geen gevoelige data op buiten je beveiligde accountomgeving. Je gegevens blijven veilig.
            </p>
            <input
              type="number"
              min={1}
              step={100}
              placeholder="Bijv. 10000"
              value={debtLockInput}
              onChange={(e) => {
                setDebtLockInput(e.target.value);
                if (debtLockError) setDebtLockError("");
              }}
              style={{
                width: "100%",
                padding: "12px 14px",
                borderRadius: 10,
                border: "1px solid #d1d5db",
                background: "#fff",
                color: "#111827",
                fontSize: 16,
                boxSizing: "border-box",
                marginBottom: 10,
              }}
            />
            {debtLockError ? (
              <p style={{ margin: "0 0 10px", color: "#b91c1c", fontSize: 13 }}>{debtLockError}</p>
            ) : null}
            <button
              type="button"
              onClick={confirmAndLockDebt}
              disabled={debtLockSaving}
              style={{
                width: "100%",
                padding: "12px 20px",
                borderRadius: 9999,
                border: "none",
                background: debtLockSaving ? "#93c5fd" : "#3399FF",
                color: "#fff",
                fontSize: 15,
                fontWeight: 600,
                cursor: debtLockSaving ? "wait" : "pointer",
              }}
            >
              {debtLockSaving ? "Bezig..." : "Verder naar de portal"}
            </button>
          </div>
        </div>
      )}
      {wegwijzerOverlayActive
        ? createPortal(
            <>
              <style>{BOX_SLIDER_GLOBAL_STYLE}</style>
              <div
                ref={wegwijzerCardRef}
                style={{
                  position: "fixed",
                  left: wegwijzerCardLayout?.left ?? "50%",
                  top: wegwijzerCardLayout?.top ?? 120,
                  width: wegwijzerCardLayout?.width ?? "min(360px, calc(100vw - 32px))",
                  maxWidth: "calc(100vw - 32px)",
                  transform: wegwijzerCardLayout?.left != null ? undefined : "translateX(-50%)",
                  zIndex: 10250,
                  boxSizing: "border-box",
                  background: "rgba(255,255,255,0.98)",
                  borderRadius: 14,
                  padding: wegwijzerMinimized ? "10px 12px" : "0 12px 12px",
                  boxShadow: "0 12px 40px rgba(15,23,42,0.2)",
                  border: "1px solid rgba(51,153,255,0.32)",
                  display: "flex",
                  flexDirection: "column",
                  touchAction: "manipulation",
                  maxHeight: wegwijzerMinimized ? undefined : "min(72vh, 520px)",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 8,
                    minHeight: 44,
                    paddingTop: wegwijzerMinimized ? 0 : 10,
                    paddingBottom: wegwijzerMinimized ? 0 : 6,
                    flexShrink: 0,
                  }}
                >
                  <div style={{ fontSize: 17, fontWeight: 700, color: "#1f2937" }}>Wegwijzer</div>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
                    <button
                      type="button"
                      aria-expanded={!wegwijzerMinimized}
                      aria-label={wegwijzerMinimized ? "Uitklappen" : "Minimaliseren"}
                      onClick={toggleWegwijzerMinimized}
                      style={{
                        width: 36,
                        height: 36,
                        borderRadius: 10,
                        border: "1px solid #cbd5e1",
                        background: "#f8fafc",
                        color: "#475569",
                        fontSize: 20,
                        lineHeight: 1,
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontWeight: 600,
                      }}
                    >
                      {wegwijzerMinimized ? "+" : "−"}
                    </button>
                    <button
                      type="button"
                      aria-label="Wegwijzer sluiten"
                      onClick={() => setWegwijzerCloseConfirmOpen(true)}
                      style={{
                        width: 36,
                        height: 36,
                        borderRadius: 10,
                        border: "1px solid #cbd5e1",
                        background: "#f8fafc",
                        color: "#64748b",
                        fontSize: 22,
                        lineHeight: 1,
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontWeight: 500,
                        padding: 0,
                      }}
                    >
                      ×
                    </button>
                  </div>
                </div>
                {!wegwijzerMinimized ? (
                  <>
                    {wegwijzerPhase === "intro" ? (
                      <>
                        <div
                          style={{
                            flex: 1,
                            minHeight: 0,
                            overflowY: "auto",
                            WebkitOverflowScrolling: "touch",
                            overscrollBehavior: "contain",
                            paddingRight: 4,
                            maxHeight: WEGWIJZER_CARD_MAX_BODY_PX,
                          }}
                        >
                          <div style={{ fontSize: 14, fontWeight: 600, color: "#3399FF", marginBottom: 6 }}>
                            {WEGWIJZER_INTRO_TITLE}
                          </div>
                          <p style={{ margin: "0 0 10px 0", fontSize: 13, lineHeight: 1.55, color: "#374151" }}>
                            {WEGWIJZER_INTRO_BODY}
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={handleWegwijzerIntroContinue}
                          style={{
                            marginTop: 10,
                            width: "100%",
                            padding: "12px 16px",
                            borderRadius: 10,
                            border: "none",
                            background: "#3399FF",
                            color: "#fff",
                            fontWeight: 600,
                            fontSize: 15,
                            cursor: "pointer",
                            flexShrink: 0,
                          }}
                        >
                          Verder
                        </button>
                      </>
                    ) : wegwijzerPhase === "wrapup" ? (
                      <>
                        <div
                          style={{
                            flex: 1,
                            minHeight: 0,
                            overflowY: "auto",
                            WebkitOverflowScrolling: "touch",
                            overscrollBehavior: "contain",
                            paddingRight: 4,
                            maxHeight: WEGWIJZER_CARD_MAX_BODY_PX,
                          }}
                        >
                          <div style={{ fontSize: 14, fontWeight: 600, color: "#3399FF", marginBottom: 6 }}>
                            {WEGWIJZER_WRAPUP_TITLE}
                          </div>
                          <p style={{ margin: "0 0 10px 0", fontSize: 13, lineHeight: 1.55, color: "#374151" }}>
                            {WEGWIJZER_WRAPUP_BODY}
                          </p>
                        </div>
                        <div
                          style={{
                            display: "flex",
                            gap: 10,
                            marginTop: 10,
                            flexShrink: 0,
                          }}
                        >
                          <button
                            type="button"
                            onClick={handleWegwijzerPrevious}
                            style={{
                              flex: 1,
                              padding: "12px 16px",
                              borderRadius: 10,
                              border: "1px solid #e5e7eb",
                              background: "#e5e7eb",
                              color: "#6b7280",
                              fontWeight: 600,
                              fontSize: 15,
                              cursor: "pointer",
                            }}
                          >
                            Vorige
                          </button>
                          <button
                            type="button"
                            onClick={handleWegwijzerWrapupDone}
                            style={{
                              flex: 1,
                              padding: "12px 16px",
                              borderRadius: 10,
                              border: "none",
                              background: "#3399FF",
                              color: "#fff",
                              fontWeight: 600,
                              fontSize: 15,
                              cursor: "pointer",
                            }}
                          >
                            Aan de slag!
                          </button>
                        </div>
                      </>
                    ) : (
                      <>
                        <div
                          style={{
                            flex: 1,
                            minHeight: 0,
                            overflowY: "auto",
                            WebkitOverflowScrolling: "touch",
                            overscrollBehavior: "contain",
                            paddingRight: 4,
                            maxHeight: WEGWIJZER_CARD_MAX_BODY_PX,
                          }}
                        >
                          {currentWegwijzerStep?.title ? (
                            <div style={{ fontSize: 14, fontWeight: 600, color: "#3399FF", marginBottom: 6 }}>
                              {currentWegwijzerStep.title}
                            </div>
                          ) : null}
                          <p style={{ margin: "0 0 10px 0", fontSize: 13, lineHeight: 1.55, color: "#374151" }}>
                            {currentWegwijzerStep?.body}
                          </p>
                          {renderWegwijzerStepInput()}
                        </div>
                        <div
                          style={{
                            display: "flex",
                            gap: 10,
                            marginTop: 10,
                            flexShrink: 0,
                          }}
                        >
                          <button
                            type="button"
                            onClick={handleWegwijzerPrevious}
                            style={{
                              flex: 1,
                              padding: "12px 16px",
                              borderRadius: 10,
                              border: "1px solid #e5e7eb",
                              background: "#e5e7eb",
                              color: "#6b7280",
                              fontWeight: 600,
                              fontSize: 15,
                              cursor: "pointer",
                            }}
                          >
                            Vorige
                          </button>
                          <button
                            type="button"
                            onClick={handleWegwijzerNext}
                            style={{
                              flex: 1,
                              padding: "12px 16px",
                              borderRadius: 10,
                              border: "none",
                              background: "#3399FF",
                              color: "#fff",
                              fontWeight: 600,
                              fontSize: 15,
                              cursor: "pointer",
                            }}
                          >
                            Doorgaan
                          </button>
                        </div>
                      </>
                    )}
                  </>
                ) : null}
              </div>
              {wegwijzerCloseConfirmOpen ? (
                <div
                  role="presentation"
                  style={{
                    position: "fixed",
                    inset: 0,
                    zIndex: 10252,
                    background: "rgba(15,23,42,0.45)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: 20,
                  }}
                  onClick={() => setWegwijzerCloseConfirmOpen(false)}
                >
                  <div
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="wegwijzer-close-confirm-title"
                    onClick={(e) => e.stopPropagation()}
                    style={{
                      width: "100%",
                      maxWidth: 340,
                      background: "#fff",
                      borderRadius: 14,
                      padding: "20px 18px",
                      boxShadow: "0 20px 50px rgba(0,0,0,0.25)",
                      boxSizing: "border-box",
                    }}
                  >
                    <p
                      id="wegwijzer-close-confirm-title"
                      style={{
                        margin: 0,
                        fontSize: 15,
                        lineHeight: 1.5,
                        color: "#1f2937",
                      }}
                    >
                      Weet je zeker dat je de wegwijzer wilt sluiten? Je kunt deze altijd opnieuw openen vanuit het menu
                    </p>
                    <div
                      style={{
                        display: "flex",
                        gap: 10,
                        marginTop: 18,
                      }}
                    >
                      <button
                        type="button"
                        onClick={() => setWegwijzerCloseConfirmOpen(false)}
                        style={{
                          flex: 1,
                          padding: "12px 14px",
                          borderRadius: 10,
                          border: "1px solid #e5e7eb",
                          background: "#f3f4f6",
                          color: "#374151",
                          fontWeight: 600,
                          fontSize: 15,
                          cursor: "pointer",
                          fontFamily: "inherit",
                        }}
                      >
                        Nee
                      </button>
                      <button
                        type="button"
                        onClick={dismissWegwijzerConfirmed}
                        style={{
                          flex: 1,
                          padding: "12px 14px",
                          borderRadius: 10,
                          border: "none",
                          background: "#3399FF",
                          color: "#fff",
                          fontWeight: 600,
                          fontSize: 15,
                          cursor: "pointer",
                          fontFamily: "inherit",
                        }}
                      >
                        Ja
                      </button>
                    </div>
                  </div>
                </div>
              ) : null}
            </>,
            document.body
          )
        : null}
    </div>
  );
}
